import { gql } from '@apollo/client';

export const GET_POSTAL = gql`
  query GetPostal(
    $code: String!
    $lat: Float
    $lng: Float
    $country: String
    $city: String
    $region: String
    $deviceId: String
    $lang: String
  ) {
    getPostal(
      input: {
        code: $code
        lat: $lat
        lng: $lng
        country: $country
        city: $city
        region: $region
        deviceId: $deviceId
        lang: $lang
      }
    ) {
      isInService
      code
      city
    }
  }
`;

/**
 * GET_POSTAL plus the address being checked, for Aperio's coverage log
 * (kept in Aperio's own database for 24 months, never sent to analytics).
 * Only the Aperio preset uses it; every other tenant sends GET_POSTAL,
 * byte for byte as before.
 */
export const GET_POSTAL_WITH_ADDRESS = gql`
  query GetPostalWithAddress(
    $code: String!
    $lat: Float
    $lng: Float
    $country: String
    $city: String
    $region: String
    $deviceId: String
    $lang: String
    $street: String
    $unit: String
    $address: String
    $placeId: String
    $lastSource: String
    $lastMedium: String
    $lastCampaign: String
    $gclid: String
    $fbclid: String
    $oppref: String
    $referrerHost: String
  ) {
    getPostal(
      input: {
        code: $code
        lat: $lat
        lng: $lng
        country: $country
        city: $city
        region: $region
        deviceId: $deviceId
        lang: $lang
        street: $street
        unit: $unit
        address: $address
        placeId: $placeId
        lastSource: $lastSource
        lastMedium: $lastMedium
        lastCampaign: $lastCampaign
        gclid: $gclid
        fbclid: $fbclid
        oppref: $oppref
        referrerHost: $referrerHost
      }
    ) {
      isInService
      code
      city
    }
  }
`;

export const UPDATE_WORK_ORDER_ADDRESS = gql`
  mutation UpdateWorkOrderAddress($id: Int!,
  $addressId: Int!,
  $mileageCharge: Int) {
    updateWorkOrderAddress(id: $id,
    addressId: $addressId,
    mileageCharge: $mileageCharge) {
      id
      address {
        street
        city
        province
        postalCode
        lat
        lng
      }
    }
  }
`;

export const GET_CURRENT_USER = gql`
  query GetCurrentUser {
    getCurrentUser {
      user {
        id
        name
        email
        notificationRequests
      }
      customerVendor {
        id
        externalCustomerVendorId
      }
    }
  }
`;

export const GET_USER_BY_ID = gql`
  query getUserById($userId: Int!) {
    getUserById(userId:$userId) {
        id
        email
        name
        discountType
        discountAmount
        vendorId
    }
  }
`;

export const GET_YEARS = gql`
  query getAllVehicleYears {
    getAllVehicleYears {
      year
    }
  }
`;

export const GET_MAKES_BY_YEAR = gql`
  query GetMakesByYear($year: String!) {
    getMakesByYear(year: $year) {
      make
    }
  }
`;

export const GET_MODELS_BY_YEAR_AND_MAKE = gql`
  query GetModelsByYearAndMake($year: String!, $make: String!) {
    getModelsByYearAndMake(year: $year, make: $make) {
      model
    }
  }
`;

export const GET_VEHICLES_BY_MAKE_MODEL_AND_YEAR = gql`
  query GetVehiclesByMakeModelAndYear(
    $make: String!
    $model: String!
    $year: Int!
  ) {
    getVehiclesByMakeModelAndYear(make: $make, model: $model, year: $year) {
      id
      make
      model
      year
      engine
      oilVolume
    }
  }
`;

export const GET_EXACT_VEHICLES_BY_MAKE_MODEL_AND_YEAR = gql`
  query GetExactVehiclesByMakeModelAndYear(
    $make: String!
    $model: String!
    $year: Int!
  ) {
    getExactVehiclesByMakeModelAndYear(
    make: $make, model: $model, year: $year) {
      id
      make
      model
      year
      engine
      oilVolume
    }
  }
`;

export const GET_VEHICLES_BY_MAKE_MODEL_AND_YEAR_AND_ENGINE = gql`
  query getVehiclesByMakeModelAndYearAndEngine(
    $make: String!
    $model: String!
    $year: Int!
    $engine: String!
  ) {
    getVehiclesByMakeModelAndYearAndEngine(
      make: $make,
      model: $model,
      year: $year,
      engine: $engine
    ) {
      id
      make
      model
      year
      engine
      oilVolume
    }
  }
`;

export const GET_SERVICE_PRICES = gql`
  query GetServicePrices(
    $vehicleId: Int! 
    $countryCode: String, 
    $customerId: Int,
    $userVehicleId: Int,
    ) {
    getServicePrices(
      vehicleId: $vehicleId, 
      countryCode: $countryCode, 
      customerId: $customerId,
      userVehicleId: $userVehicleId,
    ) {
      id
      name
      nameKey
      description
      subDescription
      value
      sortOrder
      taxName
      taxPercent
      taxId
      discountedValue
      serviceTypeId
      serviceTypeName
      active
      priceNote
      planPriceBreakdown {
        basePriceCents
        engineSurchargeCents
        engineSurchargeReason
        extraLitres
        capacitySurchargeCents
        surchargeCents
        includedLitres
        extraLitreRateCents
        surchargeRoundToCents
        totalCents
        monthlyPremiumCents
        instalmentCount
        instalmentCents
        lastInstalmentCents
        termTotalCents
      }
    }
  }
`;

export const GET_ADD_ON_PRICES = gql`
  query GetAddOnPrices($vehicleId: Int!,
  $customerId: Int,
  $userVehicleId: Int) {
    getAddOnPrices (vehicleId: $vehicleId,
    customerId: $customerId,
    userVehicleId: $userVehicleId) {
      id
      name
      nameKey
      value
      description
      sortOrder
      taxName
      taxPercent
      taxId
      discountedValue
      serviceTypeId
      serviceTypeName
      active
    }
  }
`;

export const GET_ADD_ON_SURCHARGE_PRICES = gql`
  query GetAddOnSurchargePrices(
    $id: Int!,
    $vendorId: Int,
    $customerId: Int,
    $userVehicleId: Int
  ) {
    getAddOnSurchargePrices(
      id: $id,
      vendorId: $vendorId,
      customerId: $customerId,
      userVehicleId: $userVehicleId
    ) {
      id
      name
      nameKey
      value
      description
      sortOrder
      taxName
      taxPercent
      taxId
      discountedValue
      serviceTypeId
      serviceTypeName
      active
    }
  }
`;

export const CALCULATE_VEHICLE_OIL_PRICE = gql`
  query CalculateVehicleOilPrice($vehicleId: Int!) {
    calculateVehicleOilPrice(vehicleId: $vehicleId) {
      oilPrice
      oil {
        id
        weight
        type
      }
    }
  }
`;

// Staff "Get Quotes" report: fleet accounts with a negotiated discount, and
// the oil change price one of them pays (staff roles only on the server).
export const FLEET_DISCOUNT_ACCOUNTS = gql`
  query FleetDiscountAccounts {
    fleetDiscountAccounts {
      userId
      name
      email
      percent
    }
  }
`;

export const CALCULATE_FLEET_VEHICLE_OIL_PRICE = gql`
  query CalculateFleetVehicleOilPrice($vehicleId: Int!, $fleetUserId: Int!) {
    calculateFleetVehicleOilPrice(vehicleId: $vehicleId, fleetUserId: $fleetUserId) {
      oilPrice
      fleetDiscountPercent
      fleetDiscountAmount
      fleetOilPrice
      contractPriced
      oil {
        id
        weight
        type
      }
    }
  }
`;

export const GET_ALL_SERVICES = gql`
  query GetAllServices($workOrderId: Int!) {
    getAllServices(workOrderId: $workOrderId){
      id
      name
      description
      value
    }
  }
`;

export const RECALCULATE_INSPECTION_PRICES = gql`
  query RecalculateInspectionPrices(
    $mileage: Int!,
    $userVehicleId: Int!,
    $lineItems: [InspectionLineItemInput!]!
  ) {
    recalculateInspectionPrices(
      mileage: $mileage,
      userVehicleId: $userVehicleId,
      lineItems: $lineItems
    ) {
      price
      serviceId
      discountedPrice
    }
}
`;

export const UPDATE_DISCOUNTED_LINE_ITEMS = gql`
  mutation UpdateDiscountedLineItems(
    $lineItems: [DiscountedLineItemInput!]!
  ) {
    updateDiscountedLineItems(
      lineItems: $lineItems
    ) {
      id
      discountedPrice
    }
  }
`;

export const GET_VEHICLES = gql`
  query GetVehicles {
    getVehicles {
      id
      make
      model
      year
      engine
    }
  }
`;
export const GET_VEHICLE_BY_ID = gql`
  query GetVehicleById($id: Int!) {
    getVehicleById(id: $id) {
      id
      make
      model
      year
      engine
      oilVolume
    }
  }
`;

export const GET_ADDRESSES = gql`
  query GetAddresses {
    getAddresses {
      id
      street
      city
      postalCode
      lat
      lng
      province
      country
      countryCode
      provinceCode
    }
  }
`;

export const GET_ADDRESSES_BY_USER_ID = gql`
  query GetAddressesByUserId ($userId: Int!) {
    getAddressesByUserId (userId: $userId) {
      id
      street
      city
      postalCode
      lat
      lng
      province
      country
      countryCode
      provinceCode      
    }
  }
`;

export const GET_CONTACTS = gql`
  query GetContacts{
    getContacts{
      id
      name
      email
      phoneNumber
    }
  }
`;

export const GET_VENDOR_LIST = gql`
  query GetVendors{
      getVendors{
        id
        name
        remittance
        isRemittanceOnSubTotal
      }
    }
`;

export const GET_FRANCHISE_VENDOR_LIST = gql`
  query GetFranchiseVendorList(
    $franchiseIds: [Int!]!,
    $startDate: DateTime!,
    $endDate: DateTime!
    ){
      getFranchiseVendorList(
        franchiseIds: $franchiseIds,
        startDate: $startDate,
        endDate: $endDate
      ){
        vendorId
        vendorName
      }
    }
`;

export const GET_VENDOR_BY_ID = gql`
  query GetVendorById ($vendorId: Int!){
      getVendorById(vendorId: $vendorId){
        id
        name
        remittance
        isRemittanceOnSubTotal
      }
    }
`;

export const GET_CONTACTS_BY_USER_ID = gql`
  query GetContactsByUserId ($userId: Int!){
    getContactsByUserId (userId: $userId){
      id
      name
      email
      phoneNumber
    }
  }
`;

export const GET_AVAILABILITY = gql`
  query getAvailability(
    $start: DateTime!
    $end: DateTime!
    $postalCode: GetPostalInput!
    $userTimezone: String!
    $franchiseId: Int
  ) {
    getAvailability(
      start: $start,
      end: $end,
      postalCode: $postalCode,
      userTimezone: $userTimezone,
      franchiseId: $franchiseId
    ) {
      date
      routeId
      isBooked
      priceAdjustmentPercent
      priceAdjustmentCents
      priceTier
      isLowestPrice
      startsAt
      timeZone
    }
  }
`;

/**
 * GET_AVAILABILITY plus the rules that priced each slot (consumer skin,
 * docs/consumer-skin-api.md section 5): `priceAdjustmentLabel` names the
 * lead-time rule that applied, so a 7-day-out slot is never called "Book
 * 14+ days ahead". A server without the fields refuses it; the caller asks
 * GET_AVAILABILITY.
 */
export const GET_AVAILABILITY_LABELLED = gql`
  query getAvailabilityLabelled(
    $start: DateTime!
    $end: DateTime!
    $postalCode: GetPostalInput!
    $userTimezone: String!
    $franchiseId: Int
  ) {
    getAvailability(
      start: $start,
      end: $end,
      postalCode: $postalCode,
      userTimezone: $userTimezone,
      franchiseId: $franchiseId
    ) {
      date
      routeId
      isBooked
      priceAdjustmentPercent
      priceAdjustmentCents
      priceTier
      isLowestPrice
      startsAt
      timeZone
      appliedRules
      priceAdjustmentLabel
    }
  }
`;

/**
 * The consumer calendar's availability (owner launch check 2026-10-21):
 * GET_AVAILABILITY_LABELLED with `skin: "consumer"`, so the server offers
 * only times a van can take (its own hours, no visit within the hour). A
 * server without the argument refuses it; the caller asks the labelled one.
 */
export const GET_AVAILABILITY_CONSUMER = gql`
  query getAvailabilityConsumer(
    $start: DateTime!
    $end: DateTime!
    $postalCode: GetPostalInput!
    $userTimezone: String!
    $franchiseId: Int
    $skin: String
  ) {
    getAvailability(
      start: $start,
      end: $end,
      postalCode: $postalCode,
      userTimezone: $userTimezone,
      franchiseId: $franchiseId,
      skin: $skin
    ) {
      date
      routeId
      isBooked
      priceAdjustmentPercent
      priceAdjustmentCents
      priceTier
      isLowestPrice
      startsAt
      timeZone
      appliedRules
      priceAdjustmentLabel
    }
  }
`;

/**
 * GET_AVAILABILITY for a server that does not know startsAt / timeZone yet
 * (a deploy where this client lands first): slots are then the browser's
 * own wall times, as before.
 */
export const GET_AVAILABILITY_LEGACY = gql`
  query getAvailabilityLegacy(
    $start: DateTime!
    $end: DateTime!
    $postalCode: GetPostalInput!
    $userTimezone: String!
    $franchiseId: Int
  ) {
    getAvailability(
      start: $start,
      end: $end,
      postalCode: $postalCode,
      userTimezone: $userTimezone,
      franchiseId: $franchiseId
    ) {
      date
      routeId
      isBooked
      priceAdjustmentPercent
      priceAdjustmentCents
      priceTier
      isLowestPrice
    }
  }
`;

export const GET_BOOKING_PRICING_CONFIG = gql`
  query getBookingPricingConfig(
    $franchiseId: Int
    $countryCode: String
    $provinceCode: String
    $postalCode: String
    $userVehicleId: Int
    $vehicleId: Int
  ) {
    getBookingPricingConfig(
      franchiseId: $franchiseId
      countryCode: $countryCode
      provinceCode: $provinceCode
      postalCode: $postalCode
      userVehicleId: $userVehicleId
      vehicleId: $vehicleId
    ) {
      taxRatePercent
      mobileServiceFeeCents
      mobileServiceFeeAllInclusiveCents
      mobileServiceFeeHighUseCents
      mobileServiceFeeVisitsIncluded
      planYearVisitCount
      planStartMobileFeeIncluded
      prepayDiscountPercent
      slotPricingEnabled
      bestSlotAdjustmentPercent
      bestSlotAdjustmentLabel
      discountCutoffHours
    }
  }
`;

export const CREATE_CONTACT = gql`
  mutation CreateContact(
    $name: String!
    $email: String!
    $phoneNumber: String!
  ) {
    createContact(
      input: { name: $name, email: $email, phoneNumber: $phoneNumber }
    ) {
      id
    }
  }
`;

export const UPDATE_EMAIL_PREFERENCE = gql`
  mutation UpdateEmailPreference($checkedEmail: Boolean!) {
    updateEmailPreference(checkedEmail: $checkedEmail)
  }
`;

export const UPDATE_CONTACT = gql`
  mutation UpdateContact(
    $name: String!
    $email: String!
    $phoneNumber: String!
    $id: Int!
  ) {
    updateContact(
      input: { name: $name, email: $email, phoneNumber: $phoneNumber }
      contactId: $id
    ) {
      id
      name
      email
      phoneNumber
    }
  }
`;

export const DELETE_CONTACT = gql`
  mutation DeleteContact($id: Int!) {
    deleteContact(contactId: $id) {
      id
    }
  }
`;

export const CREATE_ADDRESS = gql`
  mutation CreateAddress(
    $street: String!
    $city: String!
    $postalCode: String!
    $province: String!
    $country: String!
    $countryCode: String!
    $provinceCode: String!
    $lat: Float!
    $lng: Float!
    $name: String
  ) {
    createAddress(
      input: {
        street: $street
        city: $city
        postalCode: $postalCode
        lat: $lat
        lng: $lng
        province: $province
        country: $country
        countryCode: $countryCode
        provinceCode: $provinceCode
        name: $name
      }
    ) {
      id
    }
  }
`;

export const UPDATE_ADDRESS = gql`
  mutation UpdateAddress(
    $street: String!
    $city: String!
    $postalCode: String!
    $province: String!
    $country: String!
    $countryCode: String!
    $provinceCode: String!
    $lat: Float!
    $lng: Float!
    $id: Int!
  ) {
    updateAddress(
      input: {
        street: $street
        city: $city
        postalCode: $postalCode
        lat: $lat
        lng: $lng
        province: $province
        country: $country
        countryCode: $countryCode
        provinceCode: $provinceCode
      }
      addressId: $id
    ) {
      id
    }
  }
`;

export const DELETE_ADDRESS = gql`
  mutation DeleteAddress($id: Int!) {
    deleteAddress(addressId: $id) {
      id
    }
  }
`;

export const ADD_VEHICLE = gql`
  mutation AddVehicle($vehicleId: Int!) {
    addVehicle(vehicleId: $vehicleId) {
      id
    }
  }
`;

export const REMOVE_VEHICLE = gql`
  mutation RemoveVehicle($vehicleId: Int!) {
    removeVehicle(vehicleId: $vehicleId) {
      id
    }
  }
`;

export const DELETE_USER_VEHICLE = gql`
  mutation DeleteUserVehicle($id: Int!) {
    deleteUserVehicle(id: $id) {
      id
    }
  }
`;
export const IS_WORK_ORDER_ASSIGNED_TO_USER_VEHICLE = gql`
  mutation IsUserVehicleAssignedToWorkOrder($id: Int!) {
    isUserVehicleAssignedToWorkOrder(id: $id)
  }
`;

export const GET_WORK_ORDERS = gql`
  query GetWorkOrders($status: String!) {
    getWorkOrders(status: $status) {
      id
      vehicle {
        make
        model
        year
      }
      lineItems {
        id
        price
        tax
        quantity
        name
        description
        discountedPrice
      }
      scheduledAt
    }
  }
`;

export const CANCEL_WORK_ORDER = gql`
  mutation CancelWorkOrder(
    $workOrderId: Int!,
    $cancelReason: String!,
    $proofOfCancelLocation: String,
    $notes: String,
    ) {
    cancelWorkOrder(
      workOrderId: $workOrderId,
      cancelReason: $cancelReason,
      proofOfCancelLocation: $proofOfCancelLocation,
      notes: $notes,
    )
    {
      id
    }
  }
`;

export const DELETE_WORK_ORDER = gql`
  mutation DeleteWorkOrder($workOrderId: Int!) {
    deleteWorkOrder(workOrderId: $workOrderId)
  }
`;

export const ASSIGN_WORK_ORDER = gql`
mutation AssignWorkOrder(
  $workOrderId: Int!,
  $assignedFranchise: Int!
  $assignedVan: Int
  ) {
  assignWorkOrder(
    workOrderId: $workOrderId
    assignedFranchise: $assignedFranchise
    assignedVan: $assignedVan
    )
}
`;

export const ASSIGN_WORK_ORDER_TO_VAN = gql`
mutation AssignWorkOrderToVan(
  $workOrderId: Int!,
  $assignedVan: Int,
  ) {
  assignWorkOrderToVan(
    workOrderId: $workOrderId,
    assignedVan: $assignedVan,
    )
}
`;

export const GET_FRANCHISES_OF_FRANCHISE_MANAGER = gql`
  query getFranchisesOfFranchiseManager{
    getFranchisesOfFranchiseManager{
      id
      name
      type
      address {
      countryCode
      }
      feature {
        name
        active
      }
    }
  }
`;

export const GET_FRANCHISES_VANS_OF_FRANCHISE_MANAGER = gql`
  query getFranchisesVansOfFranchiseManager($userId: Int!){
    getFranchisesVansOfFranchiseManager(userId: $userId){
      id
      name
      vans {
        id,
        name,
      }
    }
  }
`;

export const GET_FRANCHISES_VENDORS_OF_FRANCHISE_SALES = gql`
  query getFranchisesVendorsOfFranchiseSales($userId: Int!){
    getFranchisesVendorsOfFranchiseSales(userId: $userId){
      id
      name
      vendors {
        id,
        name,
      }
    }
  }
`;

export const GET_CUSTOMERS_OF_FRANCHISE_MANAGER = gql`
  query GetCustomersOfFranchiseManager ($franchiseId: Int!){
    getCustomersOfFranchiseManager (franchiseId: $franchiseId) {
      id
      name
      email
    }
  }
`;

export const GET_ALL_CUSTOMERS = gql`
  query GetAllCustomers ($franchiseId: Int!){
    getAllCustomers (franchiseId: $franchiseId) {
      id
      name
      email
    }
  }
`;

export const CREATE_CUSTOMER = gql`
  mutation createCustomer(
    $contactInput: ContactInput!
    $password: String
    $vendorListInput: VendorListInput
  ) {
    createCustomer(
    input: {
        contact: $contactInput
        password: $password
        vendor: $vendorListInput
    }
    ) {
      id
    }
  }
`;

export const GET_PAYOUT_REPORTS_BY_DATE = gql`
  query GetPayoutStatementsByDate(
    $franchiseId: Int!
    $startDate: String!
    $endDate: String!
  ) {
    getPayoutStatementsByDate(
      franchiseId: $franchiseId
      startDate: $startDate
      endDate: $endDate
  ) {
      id
      franchiseId
      userId
      vanId
      startDate
      endDate
      status
      van {
        name
      }
      user {
        name
      }
    }
  }
`;

export const GET_PAYOUT_REPORTS_FRANCHISE_MANAGER = gql`
  query GetPayoutStatementsFranchiseManager(
    $franchiseId: Int!
    $pageNumber: Int!
    ){
    getPayoutStatementsFranchiseManager(
      franchiseId: $franchiseId
      pageNumber: $pageNumber,
      ) {
      payoutReports {
        id,
        franchiseId,
        startDate,
        endDate,
        status,
        totalSales,
        totalRevenue,
        transactionFees,
        totalTax,
        marketingFee,
        royaltyFee,
        insurance,
        technologyFee,
        thirdPartyFleetFee,
        nationalFleetFee,
        creditGoOil,
        refunds,
        cashPayments,
        total,
        isVisible,
        subTotalSales,
        subTotalRevenue
      }
      total
    }
  }
`;

export const GET_PAYOUT_REPORTS_FRANCHISE_TECHNICIAN_RANGE = gql`
  query GetPayoutStatementsTechnician(
    $franchiseId: Int!
    $userId: Int!
    $pageNumber: Int!
    ){
    getPayoutStatementsTechnician(
      franchiseId: $franchiseId
      userId: $userId
      pageNumber: $pageNumber,
      ) {
        groupedDateRanges {
        startDate
        endDate
        count
        reports {
          id
          franchiseId
          startDate
          endDate
        }
      }
      total
    }
  }
`;

export const GET_PAYOUT_REPORTS_FRANCHISE_SALES_RANGE = gql`
  query GetPayoutStatementsSales(
    $franchiseId: Int!
    $userId: Int!
    $pageNumber: Int!
    ){
    getPayoutStatementsSales(
      franchiseId: $franchiseId
      userId: $userId
      pageNumber: $pageNumber,
      ) {
        groupedDateRanges {
        startDate
        endDate
        count
        reports {
          id
          franchiseId
          startDate
          endDate
        }
      }
      total
    }
  }
`;

export const GET_PAYOUT_REPORTS_FRANCHISE_MANAGER_RANGE = gql`
  query GetPayoutStatementsFranchiseManagerRange(
    $franchiseId: Int!
    $pageNumber: Int!
    ){
    getPayoutStatementsFranchiseManagerRange(
      franchiseId: $franchiseId
      pageNumber: $pageNumber,
      ) {
        groupedDateRanges {
        startDate
        endDate
        count
        reports {
          id
          franchiseId
          startDate
          endDate
        }
      }
      total
    }
  }
`;

export const GET_PAYOUT_REPORT_FRANCHISE_MANAGER_BY_ID = gql`
  query GetPayoutReportFranchiseManagerById(
    $id: Int!
    ){
    getPayoutReportFranchiseManagerById(
      id: $id
      ) {
        id
        franchiseId
        vanId
        userId
        startDate
        endDate
        totalSales
        totalRevenue
        transactionFees
        vanRentFee
        fuelAllowance
        workOrderCount
        workOrderEarnings
        totalTax
        marketingFee
        royaltyFee
        insurance
        technologyFee
        thirdPartyFleetFee
        nationalFleetFee
        creditGoOil
        refunds
        cashPayments
        total
        status
        subTotalSales,
        subTotalRevenue
        unreportedRevenue
        invoice {
          id
          invoiceNumber
          status
        }
        franchise {
          id
          name
          address {
            countryCode
          }
          franchiseTypes {
            id
          }
      }
    }
  }
`;

export const GET_PAYOUT_REPORT_PAYMENTS = gql`
query GetPayoutReportPayments(
  $startDate: DateTime!
  $endDate: DateTime!
  $franchiseId: Int!
  $vanId: Int
  $userId: Int
  ){
  getPayoutReportPayments(
    startDate: $startDate
    endDate: $endDate
    franchiseId: $franchiseId
    vanId: $vanId
    userId: $userId
    ) {
      paymentDetails{
        id
        amount
        paymentMethod
        status
        remittanceFee
        paidAmount
        createdAt
        franchise{
          id
          name
        }
        author{
          id
          name
        }
        invoice{
          id
          invoiceNumber
          createdAt
        }
      }
    }
  }
`;
export const GET_PAYOUT_REPORT_REFUNDED_PAYMENTS = gql`
  query GetPayoutReportRefundedPayments(
    $startDate: DateTime!
    $endDate: DateTime!
    $franchiseId: Int!
    $vanId: Int
    $userId: Int
    ){
    getPayoutReportRefundedPayments(
      startDate: $startDate
      endDate: $endDate
      franchiseId: $franchiseId
      vanId: $vanId
      userId: $userId
      ) {
        paymentDetails{
          id
          amount
          paymentMethod
          status
          remittanceFee
          paidAmount
          refundedAmount
          createdAt
          updatedAt
          franchise{
            id
            name
          }
          author{
            id
            name
          }
          invoice{
            id
            invoiceNumber
            createdAt
          }
        }
      }
    }
  `;

export const GET_PAYOUT_REPORT_INVOICES = gql`
query GetPayoutReportInvoices(
  $startDate: DateTime!
  $endDate: DateTime!
  $franchiseId: Int!
  $vanId: Int
  $userId: Int
  ){
  getPayoutReportInvoices(
    startDate: $startDate
    endDate: $endDate
    franchiseId: $franchiseId
    vanId: $vanId
    userId: $userId
    ) {
      invoiceDetails{
        id
        invoiceNumber
        status
        tax
        subTotal
        discountSubTotal
        billedAmount
        createdAt
        contact{
          name
        }
        workOrder{
          id
          status
          completedAt
          poNumber
        }
        franchise{
          id
          name
        }
      }
    }
  }
`;

export const GET_INVENTORY_PART_FOR_FRANCHISE = gql`
  query GetInventoryPartForFranchise(
  $franchiseId: Int!,
  $pageNumber: Int!,
  $vanId: Int,
  $filter: String
  ) {
    getInventoryPartForFranchise(
    franchiseId: $franchiseId,
    pageNumber: $pageNumber,
    vanId: $vanId,
    filter: $filter) {
      inventoryPartItems {
        id
        partItemId
        quantity
        marketInventoryQuantity
        partItem {
          id
          name
          partType {
            name
          }
          partBrand {
            name
          }
        }
      }
      total
    }
  }
`;

export const GET_VAN_TOOLS = gql`
  query GetVanTools(
  $vanId: Int!,
  $pageNumber: Int!,
  $filter: String
  ) {
    getVanTools(
    vanId: $vanId,
    pageNumber: $pageNumber,
    filter: $filter) {
      inventoryTools {
        id
        toolId
        quantity
        tool {
          name
        }
      }
      total
    }
  }
`;

export const INCREASE_VAN_TOOL_QUANTITY = gql`
  mutation IncreaseVanToolQuantity(
    $vanToolId: Int!,
    $increment: Int!,
    ) {
      increaseVanToolQuantity(
      vanToolId: $vanToolId,
      increment: $increment,
      )
    {
      id,
      vanId,
      toolId,
      quantity,
    }
  }
`;

export const DECREASE_VAN_TOOL_QUANTITY = gql`
  mutation DecreaseVanToolQuantity(
    $vanToolId: Int!,
    $reason: String!,
    ) {
      decreaseVanToolQuantity(
      vanToolId: $vanToolId,
      reason: $reason,
      )
    {
      id,
      vanId,
      toolId,
      quantity,
    }
  }
`;

export const GET_PART_TYPES = gql`
  query GetPartTypes {
    getPartTypes {
      id
      name
    }
  }
`;

export const GET_PART_BRANDS = gql`
  query GetPartBrands {
    getPartBrands {
      id
      name
    }
  }
`;

export const GET_TIRE_BRANDS = gql`
  query GetTireBrands {
    getTireBrands {
      id
      name
    }
  }
`;

export const GET_FUELS = gql`
  query GetFuels {
    getFuels {
      id
      name
    }
  }
`;

export const DELETE_INVENTORY_HISTORY_BY_ID = gql`
  mutation DeleteInventoryHistoryById($id: Int!) {
    deleteInventoryHistoryById(id: $id)
  }
`;

export const GET_PARTS_BY_TYPE_AND_SEARCH = gql`
  query GetPartsByTypeAndSearch(
    $partTypeId: Int,
    $searchTerm: String) {
      getPartsByTypeAndSearch(
      partTypeId: $partTypeId,
      searchTerm: $searchTerm
      ) {
        id
        name
        partTypeId
        partBrand {
          name
        }
      }
    }
`;

export const ADD_PART_TO_INVENTORY = gql`
  mutation AddPartToInventory($franchiseId: Int!,
  $partId: Int!,
  $vanId: Int) {
    addPartToInventory(franchiseId: $franchiseId,
    partId: $partId,
    vanId: $vanId,
    ) {
      id
      quantity
    }
  }
`;

export const GET_OILS_BY_SEARCH = gql`
  query GetOilsBySearch($searchTerm: String) {
    getOilsBySearch(searchTerm: $searchTerm) {
      id
      type
      weight
    }
  }
`;

export const ADD_OIL_TO_INVENTORY = gql`
  mutation AddOilToInventory($franchiseId: Int!,
  $oilId: Int!,
  $vanId: Int,
  ) {
    addOilToInventory(franchiseId: $franchiseId,
    oilId: $oilId,
    vanId: $vanId,
    ) {
      id
      quantity
    }
  }
`;

export const CREATE_AND_ADD_NEW_PART_TO_INVENTORY = gql`
  mutation CreateAndAddNewPartToInventory($franchiseId: Int!,
  $name: String!,
  $partTypeId: Int!,
  $vehicleId: Int!
  $partBrandId: Int!
  $vanId: Int,
  ) {
    createAndAddNewPartToInventory(
      franchiseId: $franchiseId,
      name: $name,
      partTypeId: $partTypeId,
      vehicleId: $vehicleId
      partBrandId: $partBrandId
      vanId: $vanId,
    ) {
      id
      partItem {
        name
        id
      }
    }
  }
`;

export const INCREASE_PART_QUANTITY = gql`
  mutation IncreasePartQuantity(
    $inventoryPartItemId: Int!,
    $increment: Int!, 
    $pricePerItem: Int!, 
    $receiptImage: String!) {
      increasePartQuantity(
      inventoryPartItemId: $inventoryPartItemId,
      increment: $increment, 
      pricePerItem: $pricePerItem, 
      receiptImage: $receiptImage)
    {
      id,
      partItemId,
      quantity,
    }
  }
`;

export const TRANSFER_PART_TO_VAN = gql`
  mutation TransferPartToVan(
    $inventoryPartItemId: Int!,
    $quantity: Int!, 
    $vanId: Int!) {
      transferPartToVan(
      inventoryPartItemId: $inventoryPartItemId,
      quantity: $quantity, 
      vanId: $vanId)
    {
      id,
      partItemId,
    }
  }
`;

export const TRANSFER_PART_TO_MARKET = gql`
  mutation TransferPartToMarket(
    $inventoryPartItemId: Int!,
    $quantity: Int!) {
      transferPartToMarket(
      inventoryPartItemId: $inventoryPartItemId,
      quantity: $quantity)
    {
      id,
      partItemId,
    }
  }
`;

export const LOAD_PART_QUANTITY = gql`
  mutation LoadPartQuantity(
    $inventoryPartItemId: Int!,
    $quantiry: Int!, 
    ) {
      loadPartQuantity(
      inventoryPartItemId: $inventoryPartItemId,
      quantiry: $quantiry, 
      )
    {
      id,
      partItemId,
      quantity,
    }
  }
`;

export const TRANSFER_OIL_TO_VAN = gql`
  mutation TransferOilToVan(
    $inventoryOilItemId: Int!,
    $quantity: Float!,
    $vanId: Int!) {
      transferOilToVan(
      inventoryOilItemId: $inventoryOilItemId,
      quantity: $quantity, 
      vanId: $vanId)
    {
      id,
      oilId,
    }
  }
`;

export const TRANSFER_OIL_TO_MARKET = gql`
  mutation TransferOilToMarket(
    $inventoryOilItemId: Int!,
    $quantity: Float!) {
      transferOilToMarket(
      inventoryOilItemId: $inventoryOilItemId,
      quantity: $quantity)
    {
      id,
      oilId,
    }
  }
`;

export const INCREASE_OIL_QUANTITY = gql`
  mutation IncreaseOilQuantity(
    $inventoryOilItemId: Int!,
    $increment: Float!, 
    $pricePerItem: Int!, 
    $receiptImage: String!) {
      increaseOilQuantity(
      inventoryOilItemId: $inventoryOilItemId,
      increment: $increment, 
      pricePerItem: $pricePerItem, 
      receiptImage: $receiptImage)
    {
      id,
      oilId,
      quantity,
    }
  }
`;

export const LOAD_OIL_QUANTITY = gql`
  mutation LoadOilQuantity(
    $inventoryOilItemId: Int!,
    $quantiry: Float!, 
    ) {
      loadOilQuantity(
      inventoryOilItemId: $inventoryOilItemId,
      quantiry: $quantiry, 
      )
    {
      id,
      oilId,
      quantity,
    }
  }
`;

export const DELETE_INVENTORY_PART_ITEM = gql`
  mutation DeleteInventoryPartItem($id: Int!) {
    deleteInventoryPartItem(id: $id)
  }
`;

export const DELETE_INVENTORY_OIL_ITEM = gql`
  mutation DeleteInventoryOilItem($id: Int!) {
    deleteInventoryOilItem(id: $id)
  }
`;

export const GET_INVENTORY_OIL_FOR_FRANCHISE = gql`
  query GetInventoryOilForFranchise($franchiseId: Int!, 
  $pageNumber: Int!,
  $vanId: Int,
  $filter: String) {
    getInventoryOilForFranchise(
    franchiseId: $franchiseId,
    pageNumber: $pageNumber,
    vanId: $vanId,
    filter: $filter,) {
      inventoryOilItems {
        id
        oilId
        quantity
        marketInventoryQuantity
        oil {
          id
          weight
          type
        }
      }
      total
    }
  }
`;

export const GET_VAN_FUEL_HISTORY = gql`
  query GetVanFuelHistory(
    $pageNumber: Int!,
    $vanId: Int!,
    $filter: String
  ) {
    getVanFuelHistory(
      pageNumber: $pageNumber,
      vanId: $vanId,
      filter: $filter
    ) {
      vanFuelHistoryItems {
        id
        vanFuelId
        quantity
        pricePerItem
        createdAt
        vanFuel {
          id
          van {
            id
            name
          }
          fuel {
            id
            name
          }
        }
      }
      total
    }
  }
`;

export const ADD_VAN_FUEL = gql`
  mutation AddVanFuel(
    $fuelId: Int!,
    $vanId: Int!,
    $increment: Float!,
    $pricePerItem: Int!,
    $receiptImage: String!
  ) {
    addVanFuel(
      fuelId: $fuelId,
      vanId: $vanId,
      increment: $increment,
      pricePerItem: $pricePerItem,
      receiptImage: $receiptImage
    ) {
      id
      quantity
      pricePerItem
      createdAt
    }
  }
`;

export const UPDATE_USER_INFO = gql`
  mutation UpdateUserInfo($name: String, $email: String, $password: String) {
    updateUserInfo(input: { name: $name, email: $email, password: $password }) {
      name
      email
    }
  }
`;

export const CONFIRM_WORK_ORDER = gql`
query ConfirmWorkOrder( $id: Int!, $vanId: Int!) {
  confirmWorkOrder(id: $id, vanId: $vanId) {
    id
  }
}
`;

export const UPDATE_WORK_ORDER_VEHICLE_ID = gql`
  mutation UpdateWorkOrderVehicleId($id: Int!, $vehicleId: Int!) {
    UpdateWorkOrderVehicleId(id: $id, vehicleId: $vehicleId ) {
      id
    }
  }
`;

export const UPDATE_LINE_ITEMS = gql`
mutation UpdateLineItems(
  $serviceIds: [ServiceItemInput!]!, 
  $id: Int!
  $poNumber: String!,
  ) {
  updateLineItems(
    input:{
      serviceIds: $serviceIds
      id: $id,
      poNumber: $poNumber
    }
    ) {
    id
  }
}
`;

export const UPDATE_LINE_ITEMS_PRICES = gql`
mutation UpdateLineItemsPrices($id: Int!) {
    updateLineItemsPrices(id: $id) {
      id
    }
  }
`;

export const UPDATE_LINE_ITEM_LOCATION = gql`
mutation UpdateLineItemLocation(
  $proofOfFixLocation: [ProofOfFixInput!]!, 
  $id: Int!
  ) {
    updateLineItemLocation(
    input:{
      proofOfFixLocation: $proofOfFixLocation
      id: $id,
    }
    ) {
    id
  }
}
`;

export const GET_SMARTCAR_AUTHENTICATION_TOKEN = gql`
mutation GetSmartCarAuthentictionToken(
  $userId: Int!, 
  $code: String!
  ) {
    getSmartCarAuthentictionToken(
      userId: $userId
      code: $code,
    )
}
`;

export const UNLOCK_SMARTCAR_VEHICLE = gql`
mutation UnlockSmartCarVehicle(
  $smartcarVehicleId: String!,
  $userVehicleId: Int!
  ) {
    unlockSmartCarVehicle(
      smartcarVehicleId: $smartcarVehicleId
      userVehicleId: $userVehicleId,
    )
}
`;

export const LOCK_SMARTCAR_VEHICLE = gql`
mutation LockSmartCarVehicle(
  $smartcarVehicleId: String!,
  $userVehicleId: Int!
  ) {
    lockSmartCarVehicle(
      smartcarVehicleId: $smartcarVehicleId
      userVehicleId: $userVehicleId,
    )
}
`;

export const UPDATE_USER_VEHICLE_ID = gql`
mutation UpdateUserVehicleId($vehicleId: Int!, $userVehicleId: Int!) {
  updateUserVehicleId(vehicleId: $vehicleId, userVehicleId: $userVehicleId)
}
`;

/**
 * `idempotencyKey` (util/bookingKey) makes a repeat of the same submit
 * return the same work order (QA 2026-09-25 #1). A server that does not know
 * the field yet (a deploy where the client lands first) refuses it; the
 * Schedule step then sends CREATE_WORK_ORDER_WITHOUT_KEY, the same mutation
 * as before (see idempotencyKeyRefused).
 */
export const CREATE_WORK_ORDER = gql`
  mutation CreateWorkOrder(
    $date: DateTime!
    $password: String
    $customerId: Int
    $vehicleId: Int!
    $userVehicleId: Int
    $franchiseIdSelect: Int
    $contactInput: ContactInput!
    $addressInput: AddressInput!
    $lineItems: [LineItemInput!]!
    $vendorListInput: VendorListInput
    $vehicleCustomName: String
    $vin: String
    $potentialFranchiseId: Int
    $vanId: Int
    $discountCodeId: Int
    $discountAmount: Int
    $promoCreditAmount: Int
    $paymentPreference: String
    $expectedSlotPriceAdjustmentCents: Int
    $referralCode: String
    $attribution: BookingAttributionInput
    $idempotencyKey: String
  ) {
    createWorkOrder(
      attribution: $attribution
      input: {
        date: $date
        password: $password
        customerId: $customerId
        vehicleId: $vehicleId
        userVehicleId: $userVehicleId
        franchiseIdSelect: $franchiseIdSelect
        contact: $contactInput
        address: $addressInput
        lineItems: $lineItems
        vendor: $vendorListInput
        vehicleCustomName: $vehicleCustomName
        vin: $vin
        potentialFranchiseId: $potentialFranchiseId
        vanId: $vanId
        discountCodeId: $discountCodeId
        discountAmount: $discountAmount
        promoCreditAmount: $promoCreditAmount
        paymentPreference: $paymentPreference
        expectedSlotPriceAdjustmentCents: $expectedSlotPriceAdjustmentCents
        referralCode: $referralCode
        idempotencyKey: $idempotencyKey
      }
    ) {
      id
      vanId
      paymentPreference
      prepaymentToken
      existingAccount
      user {
        id
      }
    }
  }
`;

/**
 * The consumer skin's booking: today's CreateWorkOrder plus the consumer
 * fields (docs/consumer-skin-api.md section 2) and the unit / buzzer. Same
 * mutation, same resolver; a server that does not know a
 * field yet refuses the document, and hooks/useBookingSubmit.ts steps down
 * to the next one (..._CORE, then CREATE_WORK_ORDER).
 */
export const CREATE_WORK_ORDER_CONSUMER = gql`
  mutation CreateWorkOrderConsumer(
    $date: DateTime!
    $password: String
    $customerId: Int
    $vehicleId: Int!
    $userVehicleId: Int
    $franchiseIdSelect: Int
    $contactInput: ContactInput!
    $addressInput: AddressInput!
    $lineItems: [LineItemInput!]!
    $vendorListInput: VendorListInput
    $vehicleCustomName: String
    $vin: String
    $potentialFranchiseId: Int
    $vanId: Int
    $discountCodeId: Int
    $discountAmount: Int
    $promoCreditAmount: Int
    $paymentPreference: String
    $expectedSlotPriceAdjustmentCents: Int
    $referralCode: String
    $attribution: BookingAttributionInput
    $idempotencyKey: String
    $skin: String
    $engineStatus: String
    $engineAckHigherPrice: Boolean
    $vehicleUnverifiedText: String
    $vehicleUnverifiedKind: String
    $apSid: String
    $unit: String
    $expectedTotalCents: Int
  ) {
    createWorkOrder(
      attribution: $attribution
      input: {
        date: $date
        password: $password
        customerId: $customerId
        vehicleId: $vehicleId
        userVehicleId: $userVehicleId
        franchiseIdSelect: $franchiseIdSelect
        contact: $contactInput
        address: $addressInput
        lineItems: $lineItems
        vendor: $vendorListInput
        vehicleCustomName: $vehicleCustomName
        vin: $vin
        potentialFranchiseId: $potentialFranchiseId
        vanId: $vanId
        discountCodeId: $discountCodeId
        discountAmount: $discountAmount
        promoCreditAmount: $promoCreditAmount
        paymentPreference: $paymentPreference
        expectedSlotPriceAdjustmentCents: $expectedSlotPriceAdjustmentCents
        referralCode: $referralCode
        idempotencyKey: $idempotencyKey
        skin: $skin
        engineStatus: $engineStatus
        engineAckHigherPrice: $engineAckHigherPrice
        vehicleUnverifiedText: $vehicleUnverifiedText
        vehicleUnverifiedKind: $vehicleUnverifiedKind
        apSid: $apSid
        unit: $unit
        expectedTotalCents: $expectedTotalCents
      }
    ) {
      id
      vanId
      paymentPreference
      prepaymentToken
      existingAccount
      user {
        id
      }
    }
  }
`;

/** CREATE_WORK_ORDER_CONSUMER without `unit` and `expectedTotalCents`. */
export const CREATE_WORK_ORDER_CONSUMER_CORE = gql`
  mutation CreateWorkOrderConsumerCore(
    $date: DateTime!
    $password: String
    $customerId: Int
    $vehicleId: Int!
    $userVehicleId: Int
    $franchiseIdSelect: Int
    $contactInput: ContactInput!
    $addressInput: AddressInput!
    $lineItems: [LineItemInput!]!
    $vendorListInput: VendorListInput
    $vehicleCustomName: String
    $vin: String
    $potentialFranchiseId: Int
    $vanId: Int
    $discountCodeId: Int
    $discountAmount: Int
    $promoCreditAmount: Int
    $paymentPreference: String
    $expectedSlotPriceAdjustmentCents: Int
    $referralCode: String
    $attribution: BookingAttributionInput
    $idempotencyKey: String
    $skin: String
    $engineStatus: String
    $engineAckHigherPrice: Boolean
    $vehicleUnverifiedText: String
    $vehicleUnverifiedKind: String
    $apSid: String
  ) {
    createWorkOrder(
      attribution: $attribution
      input: {
        date: $date
        password: $password
        customerId: $customerId
        vehicleId: $vehicleId
        userVehicleId: $userVehicleId
        franchiseIdSelect: $franchiseIdSelect
        contact: $contactInput
        address: $addressInput
        lineItems: $lineItems
        vendor: $vendorListInput
        vehicleCustomName: $vehicleCustomName
        vin: $vin
        potentialFranchiseId: $potentialFranchiseId
        vanId: $vanId
        discountCodeId: $discountCodeId
        discountAmount: $discountAmount
        promoCreditAmount: $promoCreditAmount
        paymentPreference: $paymentPreference
        expectedSlotPriceAdjustmentCents: $expectedSlotPriceAdjustmentCents
        referralCode: $referralCode
        idempotencyKey: $idempotencyKey
        skin: $skin
        engineStatus: $engineStatus
        engineAckHigherPrice: $engineAckHigherPrice
        vehicleUnverifiedText: $vehicleUnverifiedText
        vehicleUnverifiedKind: $vehicleUnverifiedKind
        apSid: $apSid
      }
    ) {
      id
      vanId
      paymentPreference
      prepaymentToken
      existingAccount
      user {
        id
      }
    }
  }
`;

/**
 * The confirmation page's "Add to calendar" links (Google, and the .ics for
 * Apple / Outlook). Null unless the booking is confirmed and asked for by
 * its owner: the logged-in account, or the email it was just booked with.
 * A separate query, not a createWorkOrder field, so a client that lands
 * before the server still books (the buttons simply do not show).
 */
export const GET_BOOKING_CALENDAR_LINKS = gql`
  query BookingCalendarLinks($workOrderId: Int!, $email: String) {
    bookingCalendarLinks(workOrderId: $workOrderId, email: $email) {
      googleCalendarUrl
      icsUrl
    }
  }
`;

/** The confirmation page's "Add to Google Wallet" link (Aperio, plan bookings). */
export const GET_BOOKING_WALLET_LINK = gql`
  query BookingWalletLink($workOrderId: Int!, $email: String) {
    bookingWalletLink(workOrderId: $workOrderId, email: $email) {
      googleSaveUrl
    }
  }
`;

/** Account → Cars: the member card link of each vehicle with an active plan. */
export const GET_MY_WALLET_PASS_LINKS = gql`
  query MyWalletPassLinks {
    myWalletPassLinks {
      userVehicleId
      vehicleLabel
      googleSaveUrl
    }
  }
`;

/** Account → Cars: the licence plates of the customer's vehicles. */
export const GET_MY_VEHICLE_PLATES = gql`
  query MyVehiclePlates {
    myVehiclePlates {
      userVehicleId
      licensePlate
      registrationProvince
    }
  }
`;

/** Sets or clears (empty values) one vehicle's licence plate. */
export const UPDATE_VEHICLE_PLATE = gql`
  mutation UpdateVehiclePlate(
    $userVehicleId: Int!
    $licensePlate: String
    $registrationProvince: String
  ) {
    updateVehiclePlate(
      userVehicleId: $userVehicleId
      licensePlate: $licensePlate
      registrationProvince: $registrationProvince
    ) {
      userVehicleId
      licensePlate
      registrationProvince
    }
  }
`;

export const CREATE_WORK_ORDER_WITHOUT_KEY = gql`
  mutation CreateWorkOrderWithoutKey(
    $date: DateTime!
    $password: String
    $customerId: Int
    $vehicleId: Int!
    $userVehicleId: Int
    $franchiseIdSelect: Int
    $contactInput: ContactInput!
    $addressInput: AddressInput!
    $lineItems: [LineItemInput!]!
    $vendorListInput: VendorListInput
    $vehicleCustomName: String
    $vin: String
    $potentialFranchiseId: Int
    $vanId: Int
    $discountCodeId: Int
    $discountAmount: Int
    $promoCreditAmount: Int
    $paymentPreference: String
    $expectedSlotPriceAdjustmentCents: Int
    $referralCode: String
    $attribution: BookingAttributionInput
  ) {
    createWorkOrder(
      attribution: $attribution
      input: {
        date: $date
        password: $password
        customerId: $customerId
        vehicleId: $vehicleId
        userVehicleId: $userVehicleId
        franchiseIdSelect: $franchiseIdSelect
        contact: $contactInput
        address: $addressInput
        lineItems: $lineItems
        vendor: $vendorListInput
        vehicleCustomName: $vehicleCustomName
        vin: $vin
        potentialFranchiseId: $potentialFranchiseId
        vanId: $vanId
        discountCodeId: $discountCodeId
        discountAmount: $discountAmount
        promoCreditAmount: $promoCreditAmount
        paymentPreference: $paymentPreference
        expectedSlotPriceAdjustmentCents: $expectedSlotPriceAdjustmentCents
        referralCode: $referralCode
      }
    ) {
      id
      vanId
      paymentPreference
      prepaymentToken
      existingAccount
      user {
        id
      }
    }
  }
`;

// The customer chose to pay at the visit on the prepay page: lets the
// server report the booking's conversion as pay-at-visit (Aperio only).
export const REPORT_PREPAYMENT_SKIPPED = gql`
  mutation ReportPrepaymentSkipped($token: String!) {
    reportPrepaymentSkipped(token: $token)
  }
`;

export const CREATE_PREPAYMENT_INTENT = gql`
  mutation CreatePrepaymentIntent($token: String!) {
    createPrepaymentIntent(token: $token) {
      prepaymentId
      workOrderId
      status
      clientSecret
      amountCents
      subtotalCents
      taxCents
      currency
      stripeAccount
      paymentsUnavailable
    }
  }
`;

export const CONFIRM_PREPAYMENT = gql`
  mutation ConfirmPrepayment($token: String!) {
    confirmPrepayment(token: $token) {
      workOrderId
      status
      amountCents
      currency
      cardBrand
      cardLast4
      paidAt
      isPaid
    }
  }
`;

export const GET_PREPAYMENT_STATE = gql`
  query GetPrepaymentState($token: String!) {
    getPrepaymentState(token: $token) {
      workOrderId
      status
      amountCents
      currency
      cardBrand
      cardLast4
      paidAt
      isPaid
    }
  }
`;

export const FIND_POTENTIAL_FRANCHISE = gql`
  mutation FindPotentialFranchise(
    $addressInput: AddressInput!
    $customerId: Int
    $franchiseIdSelect: Int
  ) {
    findPotentialFranchise(
      address: $addressInput
      customerId: $customerId
      franchiseIdSelect: $franchiseIdSelect
    )
  }
`;

export const GET_MILEAGE_CHARGE = gql`
  query GetMileageChargeAddOnPrice(
    $franchiseId: Int!,
    $addressInput: AddressInput!,
    $customerId: Int,
  ) {
    getMileageChargeAddOnPrice(
      franchiseId: $franchiseId,
      address: $addressInput,
      customerId: $customerId,
    ) {
        id
        name
        nameKey
        value
        description
        sortOrder
        taxName
        taxPercent
        taxId
        discountedValue
        serviceTypeId
        serviceTypeName
    }
  }
`;

/**
 * GET_MILEAGE_CHARGE for the consumer skin: with `skin: "consumer"` the
 * Aperio server leaves out a travel charge under $5 (docs/consumer-skin-
 * api.md section 6). A server without the argument refuses it; the caller
 * then asks GET_MILEAGE_CHARGE.
 */
export const GET_MILEAGE_CHARGE_FOR_SKIN = gql`
  query GetMileageChargeAddOnPriceForSkin(
    $franchiseId: Int!,
    $addressInput: AddressInput!,
    $customerId: Int,
    $skin: String,
  ) {
    getMileageChargeAddOnPrice(
      franchiseId: $franchiseId,
      address: $addressInput,
      customerId: $customerId,
      skin: $skin,
    ) {
        id
        name
        nameKey
        value
        description
        sortOrder
        taxName
        taxPercent
        taxId
        discountedValue
        serviceTypeId
        serviceTypeName
    }
  }
`;

export const CREATE_BOOKING_ASSISTANCE_NOTIFICATION = gql`
  mutation CreateBookingAssistanceNotification(
    $name: String!
    $phoneNumber: String!
    $email: String!
    $body: String!
    $postalCode: String!
    $recaptchaToken: String!
  ) {
    createBookingAssistanceNotification(
      input: {
        message: {
          name: $name
          phoneNumber: $phoneNumber
          email: $email
          body: $body
          postalCode: $postalCode
        }
        recaptchaToken: $recaptchaToken
      }
    ) {
      name
      phoneNumber
      email
      postalCode
      body
    }
  }
`;

export const GET_FRANCHISEE_WORK_ORDERS = gql`
  query GetFranchiseeWorkOrders(
    $franchiseId: Int!,
    $status: String!,
    $pageNumber: Int!,
    $isConfirmed: Boolean,
    $vanId: Int,
    $filter: String,
  ) {
    getFranchiseeWorkOrders(
      franchiseId: $franchiseId,  
      status: $status, 
      pageNumber: $pageNumber,
      isConfirmed: $isConfirmed,
      vanId: $vanId,
      filter: $filter,
    ) {
      workOrderWithReason {
        workOrder {
          id
          franchise {
            id
            name
          }
          vehicle {
            make
            model
            year
            engine
          }
          userVehicle{
            id
            vin
            name
            smartcarVehicleId
          }
          invoice {
            status
            billedAmount
            id
          }
          lineItems {
            id
            name
            description
            price
            tax
            service {
                serviceType {
                    name
                }
            }
          }
          user{
            id
            name
            email
          }
          contact{
            id
            name
            email
            phoneNumber
          }
          scheduledAt
          completedAt
          status
          notes
        }
        reason
      }
      total
    }
  }
`;

export const GET_WORK_ORDER = gql`
  query GetWorkOrderById($id: Int!) {
    getWorkOrderById(id: $id) {
      id
      isInspected
      isConfirmed
      poNumber
      accessOutcome
      accessOutcomeContact
      address {
        street
        city
        countryCode
        postalCode
        province
        lat
        lng
      }
      contact {
        name,
        email, 
        phoneNumber
      }
      vehicle {
        id
        year
        make
        model
        engine
        oilVolume
        batteryGroupSize
        lugTorqueFtLb
        drainPlugHex
        lugNutHex
        tirePressurePsi
        recommendedTireSize {
          id
          size
        }
        recommendedOil {
          id
          type
          weight
        }
        vehiclePartItems {
          partItem {
            id
            name
            imageUrl
            partType {
              id
              name
            }
            partBrand {
              id
              name
            }
          }
        }
      }
      userVehicle {
          id
          tireSizeOverride {
            id
            size
          }
          userId
          vin
          name
          vehicleId
          smartcarVehicleId          
          subscriptions {
            id
            status
            subscriptionTypeId
          }
      }
      franchise {
        id
        name
        managerEmail
        phoneNumber
      }
      scheduledAt
      notes
      user {
        id
        email
        discountAmount
        discountType
      }
      status
      invoice {
        status
        billedAmount
        id
        invoiceNumber
      }
      van {
        id
        name
        userVans {
          user {
            email
            name
          }
        }
      }
      lineItems {
        id
        name
        price
        tax
        discountedPrice
        service {
          id
          nameKey
          invoiceDescription
            serviceType {
                name
            }
        }
      }
      inspections {
        id
        tireConditionFrontLeft
        tireConditionFrontRight
        tireConditionRearLeft
        tireConditionRearRight
        brakeConditionFrontLeft
        brakeConditionFrontRight
        brakeConditionRearLeft
        brakeConditionRearRight
        windshieldsCondition
        notes
        tireIdNumberFrontLeft
        tireIdNumberFrontRight
        tireIdNumberRearLeft
        tireIdNumberRearRight
        checkedFrontLeft
        checkedFrontRight
        checkedRearLeft
        checkedRearRight
        windshieldFluid
        brakeFluid
        leakInspection
        tireSize
        engineHours
        tireBrandId
        tireType
        tireBrandFrontLeft
        tireBrandFrontRight
        tireBrandRearLeft
        tireBrandRearRight
        tireBrandRearLeftInner
        tireBrandRearRightInner
        inspectionType
        measurementsJson
        awsLink {
          id,
          vinLink,
          mileageLink,
          oilCapLink,
          dipstickLocation,
          vehicleLocationFrontLeft,
          vehicleLocationFrontRight,
          vehicleLocationRearLeft,
          vehicleLocationRearRight,
          tinLocationFrontLeft,
          tinLocationFrontRight,
          tinLocationRearLeft,
          tinLocationRearRight,
          oilFilterLocation,
          oilLifeLocation,
          oilLifeResetLocation,
          windshieldLocation,
        }
      }
      workOrderDiscountCode {
        amountApplied
        discountCodeId
        discountCode {
          issuer
          discountType
          discountAmount
        }
      }
      userCreditTransactions {
        id
        amount
        type
      }
    }
  }
`;

export const GET_LAST_INSPECTION = gql`
  query GetLastInspection($userVehicleId: Int!, $tireType: String!) {
    getLastInspection(
      userVehicleId: $userVehicleId,
      tireType: $tireType,
      ) {
        tireType
        tireSize
        tireConditionFrontLeft
        tireConditionFrontRight
        tireConditionRearLeft
        tireConditionRearRight
        brakeConditionFrontLeft
        brakeConditionFrontRight
        brakeConditionRearLeft
        brakeConditionRearRight
        tireBrand {
          id
          name
        }
        tireBrandFrontLeft
        tireBrandFrontRight
        tireBrandRearLeft
        tireBrandRearRight
        tireBrandRearLeftInner
        tireBrandRearRightInner
        tireIdNumberFrontLeft
        tireIdNumberFrontRight
        tireIdNumberRearLeft
        tireIdNumberRearRight
        measurementsJson
      }
}`;

export const GET_CUSTOMER_WORK_ORDER = gql`
  query GetCustomerWorkOrderById($id: Int!) {
    getCustomerWorkOrderById(id: $id) {
      id
      isInspected
      isConfirmed
      poNumber
      address {
        street
        city
        countryCode
        postalCode
        province
        lat
        lng
      }
      contact {
        name,
        email,
        phoneNumber
      }
      vehicle {
        id
        year
        make
        model
        engine
        oilVolume
        batteryGroupSize
        lugTorqueFtLb
        drainPlugHex
        lugNutHex
        tirePressurePsi
        recommendedTireSize {
          id
          size
        }
        recommendedOil {
          id
          type
          weight
        }
        vehiclePartItems {
          partItem {
            id
            name
            imageUrl
            partType {
              id
              name
            }
            partBrand {
              id
              name
            }
          }
        }
      }
      userVehicle {
          id
          tireSizeOverride {
            id
            size
          }
          userId
          vin
          name
          vehicleId
          smartcarVehicleId      
          subscriptions {
            id
            status
            subscriptionTypeId
          }
      }
      franchise {
        id
        name
        managerEmail
        phoneNumber
      }
      scheduledAt
      notes
      user {
        id
        email
        discountAmount
        discountType
      }
      status
      invoice {
        status
        billedAmount
        id
        invoiceNumber
      }
      van {
        id
        name
        userVans {
          user {
            email
            name
          }
        }
      }
      lineItems {
        id
        name
        price
        tax
        discountedPrice
        service {
          id
          nameKey
          invoiceDescription
            serviceType {
                name
            }
        }
      }
      inspections {
        id
        tireConditionFrontLeft
        tireConditionFrontRight
        tireConditionRearLeft
        tireConditionRearRight
        brakeConditionFrontLeft
        brakeConditionFrontRight
        brakeConditionRearLeft
        brakeConditionRearRight
        windshieldsCondition
        notes
        tireIdNumberFrontLeft
        tireIdNumberFrontRight
        tireIdNumberRearLeft
        tireIdNumberRearRight
        checkedFrontLeft
        checkedFrontRight
        checkedRearLeft
        checkedRearRight
        windshieldFluid
        brakeFluid
        leakInspection
        tireSize
        engineHours
        inspectionType
        awsLink {
          id,
          vinLink,
          mileageLink,
          oilCapLink,
          dipstickLocation,
          vehicleLocationFrontLeft,
          vehicleLocationFrontRight,
          vehicleLocationRearLeft,
          vehicleLocationRearRight,
          tinLocationFrontLeft,
          tinLocationFrontRight,
          tinLocationRearLeft,
          tinLocationRearRight,
          oilFilterLocation,
          oilLifeLocation,
          oilLifeResetLocation,
          windshieldLocation,
        }
      }
      workOrderDiscountCode {
        amountApplied
        discountCodeId
        discountCode {
          issuer
          discountType
          discountAmount
        }
      }
      userCreditTransactions {
        id
        amount
        type
      }
    }
  }
`;

export const GET_TECHNICIAN_VANS = gql`
  query GetTechnicianVans($technicianId: Int!) {
    getTechnicianVans(technicianId: $technicianId) {
      id
      name
      franchiseId
    }
  }
`;

export const GET_WORK_ORDER_BY_DATE_AND_CONTACT = gql`
  query GetWorkOrderByDateAndContact(
    $date: DateTime!,
    $contactName: String!
    ) {
    getWorkOrderByDateAndContact(
      date: $date,
      contactName: $contactName,
      ) {
      id
      poNumber
      contact {
        name,
        email, 
        phoneNumber
      }
      scheduledAt
      notes
      status
    }
  }
`;

export const UPDATE_WORK_ORDER_PO_NUMBER = gql`
  mutation UpdateWorkOrderPoNumber($id: Int!, $poNumber: String!) {
    updateWorkOrderPoNumber(id: $id, poNumber: $poNumber ) {
      id
      poNumber
    }
  }
`;

export const UPDATE_WORK_ORDER_NOTES = gql`
  mutation UpdateWorkOrderNotes($id: Int!, $notes: String!) {
    updateWorkOrderNotes(id: $id, notes: $notes ) {
      id
      notes
    }
  }
`;

export const UPDATE_SCHEDULED_TIME = gql`
  mutation UpdateScheduledTime($id: Int!, $scheduledAt: DateTime!) {
    updateScheduledTime(id: $id, scheduledAt: $scheduledAt) {
      workOrder {
        id
      }
      reason
    }
  }
`;

export const GET_UNAVAILABILITY_REASON = gql`
  query GetUnavailabilityReason($id: Int!) {
    getUnavailabilityReason(id: $id) {
      workOrder {
        id
      }
      reason
    }
  }
`;

export const BILL_WORK_ORDER = gql`
  mutation BillWorkOrder(
    $workOrderId:Int!,
    $discountAmount:Int,
    $discountType:String
    $subscriptionTypeId:Int
    $poNumber:String
    $discountCodeId:Int
    $discountCodeAmount:Int
    $promoCreditAmount:Int
    $discountReason:String
  ) {
    billWorkOrder(
      workOrderId:$workOrderId,
      discountAmount:$discountAmount,
      discountType:$discountType
      subscriptionTypeId:$subscriptionTypeId
      poNumber:$poNumber
      discountCodeId:$discountCodeId
      discountCodeAmount:$discountCodeAmount
      promoCreditAmount:$promoCreditAmount
      discountReason:$discountReason
    ) {
      invoiceId
    }
  }
`;

// What happened on a visit that could NOT be completed — arrived, turned
// away, unit not presented, keys not released — and who the tech spoke to.
export const RECORD_VISIT_OUTCOME = gql`
  mutation RecordVisitOutcome($workOrderId: Int!, $outcome: String!, $contact: String) {
    recordVisitOutcome(workOrderId: $workOrderId, outcome: $outcome, contact: $contact) {
      id
      accessOutcome
      accessOutcomeContact
      notes
    }
  }
`;

export const UPDATE_INVENTORY_AFTER_INSPECTION = gql`
  mutation UpdateInventoryAfterInspection(
    $workOrderId:Int!,
    $inventoryOilFilterId: Int,
    $inventoryCabinFilterId: Int,
    $inventoryEngineFilterId: Int,
    $oilId: Int,
  ) {
    updateInventoryAfterInspection(
      workOrderId:$workOrderId,
      inventoryOilFilterId:$inventoryOilFilterId,
      inventoryCabinFilterId:$inventoryCabinFilterId,
      inventoryEngineFilterId:$inventoryEngineFilterId, 
      oilId:$oilId,
    )
  }
`;

export const GET_SERVICES_REQUIRED_PARTS = gql`
  query GetServiceRequiredParts(
    $serviceIds: [Int!]!
  ) {
    getServicesRequiredParts(serviceIds: $serviceIds) {
      id
      name
    }
  }
`;

export const GET_SERVICES_REQUIRED_OIL = gql`
  query GetServicesRequiredOil(
    $serviceIds: [Int!]!
    $vehicleId: Int!
  ) {
    getServicesRequiredOil(
      serviceIds: $serviceIds,
      vehicleId: $vehicleId
    ){
      recommendedOilId
      oilVolume
    }
  }
`;

export const FIND_AVAILABLE_PART_ITEMS = gql`
  query FindAvailablePartItems(
    $requiredPartsIds: [Int!]!
    $franchiseId: Int!
    $vanId: Int!
    $vehicleId: Int!
  ) {
    findAvailablePartItems(
      requiredPartsIds: $requiredPartsIds
      franchiseId: $franchiseId
      vanId: $vanId
      vehicleId: $vehicleId
    ) {
      partTypeId
      availablePartItems {
        inventoryPartItemId
        partItemId
        partItemName
        partItemBrand
        quantityAvailable
        isPreferred
      }
    }
  }
`;

export const FIND_AVAILABLE_OILS = gql`
  query FindAvailableOils(
    $franchiseId: Int!,
    $vanId: Int!
  ) {
      findAvailableOils(
        franchiseId: $franchiseId,
        vanId: $vanId
      ) {
        id
        oilId
        quantity
        oil {
          id
          weight
          type
        }
      }
  }
`;

export const CREATE_MILEAGE = gql`
  mutation CreateMileage(
    $userVehicleId:Int!, 
    $mileage:Int!,
    $workOrderId: Int,
    $eventType:String,
    $force:Boolean
  ) {
    createMileage(
      userVehicleId:$userVehicleId,
      mileage:$mileage,
      workOrderId:$workOrderId,
      eventType:$eventType,
      force:$force
    ) {
      id
    }
  }
`;

export const GET_PRESIGNED_URL = gql`
  mutation GetPresignedUrl(  
    $fileName:String!, 
  ) {
    getPresignedUrl(
      fileName:$fileName,
    ) {
      presignedUrl,
    }
  }
`;

export const CREATE_AWS_PATH = gql`
  mutation CreateAwsPath(
    $id:Int!, 
    $inspectionId:Int,
    $vinLocation:String,
    $mileageLocation:String,
    $vehicleLocation:String,
    $oilCapLocation:String,
    $dipstickLocation:String,
    $vehicleLocationFrontLeft: String,
    $vehicleLocationFrontRight: String,
    $vehicleLocationRearLeft: String,
    $vehicleLocationRearRight: String,
    $tinLocationFrontLeft: String,
    $tinLocationFrontRight: String,
    $tinLocationRearLeft: String,
    $tinLocationRearRight: String,
    $oilFilterLocation: String,
    $oilLifeLocation: String,
    $oilLifeResetLocation: String,
    $windshieldLocation: String,
  ) {
    createAwsPath(
      id:$id,
      inspectionId:$inspectionId,
      vinLocation:$vinLocation,
      mileageLocation:$mileageLocation,
      vehicleLocation:$vehicleLocation,
      oilCapLocation:$oilCapLocation,
      dipstickLocation:$dipstickLocation,
      vehicleLocationFrontLeft:$vehicleLocationFrontLeft,
      vehicleLocationFrontRight:$vehicleLocationFrontRight,
      vehicleLocationRearLeft:$vehicleLocationRearLeft,
      vehicleLocationRearRight:$vehicleLocationRearRight,
      tinLocationFrontLeft:$tinLocationFrontLeft,
      tinLocationFrontRight:$tinLocationFrontRight,
      tinLocationRearLeft:$tinLocationRearLeft,
      tinLocationRearRight:$tinLocationRearRight,
      oilFilterLocation:$oilFilterLocation,
      oilLifeLocation:$oilLifeLocation,
      oilLifeResetLocation:$oilLifeResetLocation,
      windshieldLocation:$windshieldLocation,
    ) {
      id
    }
  }
`;

export const UPDATE_USER_VEHICLE_VIN_WITH_ID = gql`
  mutation UpdateUserVehicleVinWithId($userVehicleId:Int!, $vin:String!) {
    updateUserVehicleVinById (
      userVehicleId:$userVehicleId
      vin:$vin
    ) {
      id
    }
  }
`;

export const UPDATE_USER_VEHICLE_NAME_WITH_ID = gql`
  mutation UpdateUserVehicleNameWithId($userVehicleId:Int!, $name:String!) {
    updateUserVehicleNameById (
      userVehicleId:$userVehicleId
      name:$name
    ) {
      id
    }
  }
`;

export const SET_USER_DEFAULT_DISCOUNT = gql`
  mutation SetUserDefaultDiscount(
    $userId:Int!, 
    $discountAmount:String!, 
    $discountType:String!
  ) {
    setUserDefaultDiscount(
      userId:$userId
      discountAmount:$discountAmount
      discountType:$discountType
    ) {
      success
    }
  }
`;

export const REMOVE_USER_DEFAULT_DISCOUNT = gql`
  mutation RemoveUserDefaultDiscount($userId:Int!) {
    removeUserDefaultDiscount(
      userId:$userId
    ) {
      success
    }
  }
`;

export const FIND_ALL_USER_VEHICLES_BY_CURRENT_USER = gql`
  query FindAllUserVehiclesByCurrentUser {
    findAllUserVehiclesByCurrentUser {
      vehicle {
        id
        make 
        model
        year
        engine
      }
      id
      vin
      name
      smartcarVehicleId
      mileage {
        mileage
      }
      subscriptions {
        id
        status
        userVehicleId
      }
      workOrders {
        id
        completedAt
      }
    }
  }
`;

export const FIND_ALL_USER_VEHICLES_BY_USER_ID = gql`
  query FindAllUserVehiclesByUserId ($userId: Int!) {
    findAllUserVehiclesByUserId (userId: $userId) {
      vehicle {
        id
        make 
        model
        year
        engine
      }
      id
      vin
      name
      workOrders {
        id
        completedAt
      }
      mileage {
        mileage
      }
      subscriptions {
        status
      }
    }
  }
`;

export const GET_SUBSCRIPTIONS = gql`
  query GetSubscriptions($userVehicleId: Int!) {
    getSubscriptions(userVehicleId: $userVehicleId) {
      id
      status
      userVehicleId
      createdAt
      renewedAt
      cancelEffectiveAt
      prepaidLastPaymentCents
      updatedAt
      invoices {
        id
        status
      }
      subscriptionTypeId
      subscriptionType {
        id
        name
        description
        price
        frequency
        aliasName
        active
      }
    }
  }
`;

export const PAY_LATER_SUBSCRIPTION = gql`
  mutation PayLaterSubscription($subscriptionId: Int!) {
    payLaterSubscription(subscriptionId: $subscriptionId)
  }
`;

export const CANCEL_SUBSCRIPTION = gql`
  mutation CancelSubscription($id: Int!, $cancelEffectiveAt: DateTime) {
    cancelSubscription(id: $id, cancelEffectiveAt: $cancelEffectiveAt)
  }
`;

export const UNDO_CANCEL_SUBSCRIPTION = gql`
  mutation CancelSubscription($id: Int!) {
    undoCancelSubscription(id: $id)
  }
`;

export const FIND_OR_CREATE_STRIPE_CUSTOMER = gql`
  mutation FindOrCreateStripeCustomer(
    $customerVendorInput: CustomerVendorInput!
    $userId: Int!
    ) {
      findOrCreateStripeCustomer(
        customerVendorInput: $customerVendorInput
        userId: $userId
      ) {
        id
        userId
        externalCustomerVendorId
        vendorName
      }
    }
`;

export const FIND_CUSTOMER_VENDOR = gql`
query findCustomerVendor(
  $userId: Int!
  ) {
    findCustomerVendor(
      userId: $userId
    ) {
        id
        externalCustomerVendorId
    }
  }
`;

export const CREATE_PAYMENT_METHOD_VENDOR = gql`
  mutation CreatePaymentMethodVendor(
    $paymentMethodVendorInput: PaymentMethodVendorInput!
    $userId: Int!
  ) {
  createPaymentMethodVendor(
    paymentMethodVendorInput: $paymentMethodVendorInput
    userId: $userId
  ) {
    id
    externalPaymentMethodId
    }
  }
`;

export const GET_CUSTOMER_PAYMENT_METHODS = gql`
  query getCustomerPaymentMethods(
    $customerVendorId: String!
    $countryAccount: String!
    ) {
      getCustomerPaymentMethods(
        customerVendorId: $customerVendorId
        countryAccount: $countryAccount
      ) {
        expYear
        last4
        brand
      }
    }
`;

export const GET_INVOICE = gql`
  query GetInvoiceById($invoiceId: Int!) {
    getInvoiceById(invoiceId: $invoiceId) {
      id
      invoiceNumber
      user {
        email
        id
        vendorId
      }
      contact {
        name
      }
      address {
        street
        city
        countryCode
      }
      franchise {
        name
        id
      }
      status
      tax
      billedAmount
      workOrder {
        id
      }
      payment {
        id
        amount
        status
      }
      subTotal
      discountSubTotal
      discountAmount
      discountCodeTaxAmount
      remainingAmount
    }
  }
`;

export const GET_INVOICE_BY_INVOICE_NUMBER = gql`
  query GetInvoiceByInvoiceNumber($invoiceNumber: String!) {
    getInvoiceByInvoiceNumber(invoiceNumber: $invoiceNumber) {
      id
      invoiceNumber
      user {
        email
        id
        vendorId
      }
      contact {
        name
      }
      address {
        street
        city
        countryCode
      }
      franchise {
        name
        id
      }
      status
      tax
      billedAmount
      workOrder {
        id
      }
      payment {
        id
        amount
        status
      }
      subTotal
      discountSubTotal
      discountAmount
      discountCodeTaxAmount
      remainingAmount
      tenantId
    }
  }
`;

export const PAY_BILL = gql`
  mutation PayBill(
    $invoiceId: Int!
    $userId: Int!
    $authorId: Int!
    $franchiseId: Int!
    $amount: Int!
    $paymentMethod: String!
    $chequeNumber: String
    $transitNumber: String
    $cardType: String
    $cardFourDigit: String
    $expiryMonth: String
    $expiryYear: String
    $notes: String
    $externalConfirmationNumber: String
  ) {
    payBill(
      invoiceId: $invoiceId
      input: {
        userId: $userId
        authorId: $authorId
        franchiseId: $franchiseId
        amount: $amount
        paymentMethod: $paymentMethod
        chequeNumber: $chequeNumber
        transitNumber: $transitNumber
        cardType: $cardType
        cardFourDigit: $cardFourDigit
        expiryMonth: $expiryMonth
        expiryYear: $expiryYear
        notes: $notes
        externalConfirmationNumber: $externalConfirmationNumber
      }
    ) {
      paymentId
    }
  }
`;

export const VOID_INVOICE = gql`
  mutation VoidInvoiceFront($id: Int!) {
    voidInvoiceFront(id: $id) {
      id
    }
  }
`;

export const REFUND_PAYMENT = gql`
  mutation RefundPayment($invoiceId: Int!, $amount: Int!) {
    refundPayment(invoiceId: $invoiceId, amount: $amount)
  }
`;

export const GET_READERS_BY_FRANCHISE = gql`
  query ListReadersByFranchiseId($franchiseId: Int!) {
    listReadersByFranchiseId(franchiseId: $franchiseId) {
      id,
      label,
      serialNumber
    }
  }
`;

export const SEND_PAYMENT_TO_TERMINAL = gql`
  mutation SendPaymentToTerminal($invoiceId:Int!, $readerId:String!) {
    sendPaymentToTerminal(invoiceId: $invoiceId, readerId:$readerId) {
      paymentIntentId
    }
  }
`;

export const CANCEL_TERMINAL_PAYMENT = gql`
  mutation CancelTerminalPayment($readerId:String!, $invoiceId: Int!) {
    cancelTerminalPayment(readerId: $readerId, invoiceId: $invoiceId) {
      id
    }
  }
`;

export const PAY_CASH_APP = gql`
  mutation PayCashApp($invoiceId:Int!) {
    payCashApp(invoiceId: $invoiceId) {
      paymentId,
      label,
    }
  }
`;

export const PAY_BILL_LATER = gql`
  mutation PayBillLater($invoiceId:Int!) {
    payBillLater(invoiceId: $invoiceId)
  }
`;

export const PAY_BILLS_LATER = gql`
  mutation PayBillsLater($invoiceIds: [Int!]!) {
    payBillsLater(invoiceIds: $invoiceIds)
  }
`;

export const PAY_PAYOUT_REPORT = gql`
mutation PayPayoutReport($payoutReportId:Int!) {
  payPayoutReport(payoutReportId: $payoutReportId){
    checkOutId
  }
}
`;

export const VALIDATE_DISCOUNT_CODE = gql`
  query ValidateDiscountCode(
    $code: String!,
    $services: [ServiceInput!]!
    $carId: Int!,
    $userId: Int,
    $email: String,
    $isDsp: Boolean,
    $isInspection: Boolean
  ) {
    validateDiscountCode(
      code: $code,
      services: $services
      carId: $carId
      userId: $userId
      email: $email
      isDsp: $isDsp
      isInspection: $isInspection
      ) {
        valid
        message
        discountAmount
        discountType
        discountCodeId
      }
    }
`;

// The same check for a visitor who is not logged in (book as guest, or an
// account not created yet): keyed by the email typed on the Schedule step.
export const VALIDATE_DISCOUNT_CODE_FOR_GUEST = gql`
  query ValidateDiscountCodeForGuest(
    $code: String!,
    $email: String!,
    $services: [ServiceInput!]!
    $carId: Int!
  ) {
    validateDiscountCodeForGuest(
      code: $code
      email: $email
      services: $services
      carId: $carId
    ) {
      valid
      message
      discountAmount
      discountType
      discountCodeId
    }
  }
`;

export const CREATE_INSPECTION = gql`
  mutation CreateInspection(
    $workOrderId: Int!,
    $userVehicleId:Int!,
    $lat: Float,
    $lng: Float,
    $tireConditionFrontLeft: Int, 
    $tireConditionFrontRight: Int, 
    $tireConditionRearLeft: Int, 
    $tireConditionRearRight: Int, 
    $brakeConditionFrontLeft: Int, 
    $brakeConditionFrontRight: Int, 
    $brakeConditionRearLeft: Int, 
    $brakeConditionRearRight: Int, 
    $windshieldsCondition: Int, 
    $tireSize: String,
    $engineHours: Int,
    $notes: String
    $tireIdNumberFrontLeft: Int, 
    $tireIdNumberFrontRight: Int,
    $tireIdNumberRearLeft: Int,
    $tireIdNumberRearRight: Int,
    $checkedFrontLeft: Boolean,
    $checkedFrontRight: Boolean,
    $checkedRearLeft: Boolean,
    $checkedRearRight: Boolean,
    $windshieldFluid: Boolean,
    $brakeFluid: Boolean,
    $leakInspection: Boolean,
    $tireBrandId: Int,
    $tireType: String,
    $preServiceOilLife: Int,
    $oilLifeSource: String,
    $stickerDueKm: Int,
    $stickerDueDate: String
    $oilLifeReset: Boolean,
    $isDually: Boolean,
    $tireConditionRearLeftInner: Int,
    $tireConditionRearRightInner: Int,
    $tireIdNumberRearLeftInner: Int,
    $tireIdNumberRearRightInner: Int,
    $rotationStatus: String,
    $rotationPattern: String,
    $rotationDeferReason: String,
    $rotationDeferNote: String,
    $tireBrandFrontLeft: String,
    $tireBrandFrontRight: String,
    $tireBrandRearLeft: String,
    $tireBrandRearRight: String,
    $tireBrandRearLeftInner: String,
    $tireBrandRearRightInner: String,
    $measurementsJson: String,
  ) {
    createInspection(
      workOrderId: $workOrderId,
      userVehicleId: $userVehicleId,
      lat: $lat,
      lng: $lng,
      tireConditionFrontLeft: $tireConditionFrontLeft,
      tireConditionFrontRight: $tireConditionFrontRight,
      tireConditionRearLeft: $tireConditionRearLeft,
      tireConditionRearRight: $tireConditionRearRight,
      brakeConditionFrontLeft: $brakeConditionFrontLeft,
      brakeConditionFrontRight: $brakeConditionFrontRight,
      brakeConditionRearLeft: $brakeConditionRearLeft,
      brakeConditionRearRight: $brakeConditionRearRight,
      windshieldsCondition: $windshieldsCondition,
      tireSize: $tireSize,
      engineHours: $engineHours,
      notes: $notes
      tireIdNumberFrontLeft: $tireIdNumberFrontLeft,
      tireIdNumberFrontRight: $tireIdNumberFrontRight,
      tireIdNumberRearLeft: $tireIdNumberRearLeft,
      tireIdNumberRearRight: $tireIdNumberRearRight,
      checkedFrontLeft: $checkedFrontLeft,
      checkedFrontRight: $checkedFrontRight,
      checkedRearLeft: $checkedRearLeft,
      checkedRearRight: $checkedRearRight,
      windshieldFluid: $windshieldFluid,
      brakeFluid: $brakeFluid,
      leakInspection: $leakInspection,
      tireBrandId: $tireBrandId,
      tireType: $tireType,
      preServiceOilLife: $preServiceOilLife,
      oilLifeSource: $oilLifeSource,
      stickerDueKm: $stickerDueKm,
      stickerDueDate: $stickerDueDate
      oilLifeReset: $oilLifeReset,
      isDually: $isDually,
      tireConditionRearLeftInner: $tireConditionRearLeftInner,
      tireConditionRearRightInner: $tireConditionRearRightInner,
      tireIdNumberRearLeftInner: $tireIdNumberRearLeftInner,
      tireIdNumberRearRightInner: $tireIdNumberRearRightInner,
      rotationStatus: $rotationStatus,
      rotationPattern: $rotationPattern,
      rotationDeferReason: $rotationDeferReason,
      rotationDeferNote: $rotationDeferNote,
      tireBrandFrontLeft: $tireBrandFrontLeft,
      tireBrandFrontRight: $tireBrandFrontRight,
      tireBrandRearLeft: $tireBrandRearLeft,
      tireBrandRearRight: $tireBrandRearRight,
      tireBrandRearLeftInner: $tireBrandRearLeftInner,
      tireBrandRearRightInner: $tireBrandRearRightInner,
      measurementsJson: $measurementsJson,
    ) {
      id
    }
  }
`;

export const GET_TIRE_SIZE_SUGGESTIONS = gql`
  query GetTireSizeSuggestions($userVehicleId: Int!) {
    getTireSizeSuggestions(userVehicleId: $userVehicleId) {
      recommended
      forVehicleModel
      all
    }
  }
`;

export const GET_VEHICLE_IS_DUALLY = gql`
  query GetVehicleIsDually($userVehicleId: Int!) {
    getVehicleIsDually(userVehicleId: $userVehicleId)
  }
`;

export const GET_LAST_OIL_LIFE_CAPTURE = gql`
  query GetLastOilLifeCapture($userVehicleId: Int!) {
    getLastOilLifeCapture(userVehicleId: $userVehicleId)
  }
`;

export const CREATE_ADDITIONAL_INSPECTION = gql`
  mutation CreateAdditionalInspection(
    $workOrderId: Int!,
    $userVehicleId:Int!,
    $tireConditionFrontLeft: Int, 
    $tireConditionFrontRight: Int, 
    $tireConditionRearLeft: Int, 
    $tireConditionRearRight: Int, 
    $tireSize: String,
    $tireIdNumberFrontLeft: Int, 
    $tireIdNumberFrontRight: Int,
    $tireIdNumberRearLeft: Int,
    $tireIdNumberRearRight: Int,
    $tireBrandId: Int,
    $tireType: String,
  ) {
    createAdditionalInspection(
      workOrderId: $workOrderId,
      userVehicleId: $userVehicleId,
      tireConditionFrontLeft: $tireConditionFrontLeft,
      tireConditionFrontRight: $tireConditionFrontRight,
      tireConditionRearLeft: $tireConditionRearLeft,
      tireConditionRearRight: $tireConditionRearRight,
      tireSize: $tireSize,
      tireIdNumberFrontLeft: $tireIdNumberFrontLeft,
      tireIdNumberFrontRight: $tireIdNumberFrontRight,
      tireIdNumberRearLeft: $tireIdNumberRearLeft,
      tireIdNumberRearRight: $tireIdNumberRearRight,
      tireBrandId: $tireBrandId,
      tireType: $tireType,
    ) {
      id
    }
  }
`;

export const DELETE_ADDITIONAL_INSPECTION = gql`
  mutation DeleteAdditionalInspection($inspectionId: Int!) {
    deleteAdditionalInspection(inspectionId: $inspectionId)
  }
`;

export const GET_SUBSCRIPTION_TYPES = gql`
  query GetAllSubscriptionTypes($userVehicleId: Int!) {
    getAllSubscriptionTypes(userVehicleId: $userVehicleId) {
      id
      name
      description
      price
      frequency
      aliasName
      serviceId
    }
  }
`;

export const GET_USER_VEHICLE_LATEST_MILEAGE = gql`
  query GetLatestMileageByUserVehicleId($userVehicleId: Int!) {
    getLatestMileageByUserVehicleId(userVehicleId: $userVehicleId) {
      mileage
      createdAt
    }
  }
`;

export const GET_VEHICLE_SERVICES_BY_NAME_KEYS = gql`
  query GetVehicleServicesByNameKeys(
  $userVehicleId: Int!,
  $nameKeys: [String!]!
  ) {
    getVehicleServicesByNameKeys(
    userVehicleId: $userVehicleId,
    nameKeys: $nameKeys) {
      keyName
      name
      intervalKm
      lastServicedKm
      lastServicedAt
    }
  }
`;

export const GET_FRANCHISE_ANALYTICS = gql`
  query GetFranchiseAnalytics($franchiseIds: [Int!]!) {
    getFranchiseAnalytics(franchiseIds: $franchiseIds) {
      totalSales
      totalSalesLastMonth
      totalRevenue
      totalRevenueLastMonth
      totalWorkOrders
      totalWorkOrdersLastMonth
      totalOilChanges
      totalOilChangesLastMonth
    }
  }
`;

export const GET_FRANCHISE_SALES_ANALYTICS = gql`
  query getFranchiseSalesAnalytics($franchiseIds: [Int!]!) {
    getFranchiseSalesAnalytics(franchiseIds: $franchiseIds) {
      totalSales
      totalSalesLastMonth
      totalRevenue
      totalRevenueLastMonth
      totalWorkOrders
      totalWorkOrdersLastMonth
      totalEarnings
      totalEarningsLastMonth
    }
  }
`;

export const GET_ALL_ANALYTICS = gql`
query GetAllAnalytics(
    $selectedFranchises: [Int!]!,
    $startDate: DateTime!,
    $endDate: DateTime!,
    $filter: String!,
    $selectedVans: [Int!]
  ) {
    getAllAnalytics(
      selectedFranchises: $selectedFranchises,
      startDate: $startDate,
      endDate: $endDate,
      selectedVans: $selectedVans,
      filter: $filter
    ) {
    salesByDays {
      date,
      total,
    }
    workOrderCnt
    revenueByDays {
      date,
      total,
    }
    profitMarginByDays {
      date,
      franchiseType,
      billedAmount,
      subtotal,
      tax,
      oilCost,
      filterCosts,
      staffCost,
      fuelCost,
      totalCosts,
      inventoryCosts,
      profit,
    }
    newCustomers
    recurringCustomers
    residentialCustomers
    vendorCustomers{
      vendorCustomers,
      vendorTypes {
        vendorId,
        vendorName,
      }
    }
    paymentTypes {
      paymentMethod,
      count,
    }
  }
}
`;

export const GET_ALL_SALES_ANALYTICS = gql`
query getAllSalesAnalytics(
    $selectedFranchises: [Int!]!,
    $startDate: DateTime!,
    $endDate: DateTime!,
    $filter: String!,
    $selectedVendors: [Int!]
  ) {
    getAllSalesAnalytics(
      selectedFranchises: $selectedFranchises,
      startDate: $startDate,
      endDate: $endDate,
      selectedVendors: $selectedVendors,
      filter: $filter
    ) {
    salesByDays {
      date,
      total,
    }
    workOrderCnt
    revenueByDays {
      date,
      total,
    }
    earningsByDays {
      date,
      total,
    }
    workOrders {
      assigned,
      open,
      paid,
    }
    newCustomers
    recurringCustomers
  }
}
`;

export const GET_LAST_SALES_USER_WORK_ORDERS = gql`
query getLastSalesUserWorkOrders(
    $selectedFranchises: [Int!]!,
    $selectedVendors: [Int!]!,
  ) {
    getLastSalesUserWorkOrders(
      selectedFranchises: $selectedFranchises,
      selectedVendors: $selectedVendors
    ) {
      id,
      scheduledAt,
      status,
      billedAmount,
      vendorId,
      vendorName,
      franchiseId,
      franchiseName
    }
  }
`;

export const GET_AR_REPORT = gql`
  query GetArReport(
    $vendorIds: [Int!]!,
    $startDate: DateTime!,
    $endDate: DateTime!
  ) {
    getArReport(
      vendorIds: $vendorIds,
      startDate: $startDate,
      endDate: $endDate
    ) {
      invoiceNumber
      workOrderId
      franchiseName
      contactName
      contactEmail
      status
      billedAmount
      createdAt
      }
  }
`;

export const GET_SALES_AR_REPORT = gql`
  query getSalesArReport(
    $userId: Int!,
    $franchiseIds: [Int!]!,
    $vendorIds: [Int!]!,
    $startDate: DateTime!,
    $endDate: DateTime!
  ) {
    getSalesArReport(
      userId: $userId,
      franchiseIds: $franchiseIds,
      vendorIds: $vendorIds,
      startDate: $startDate,
      endDate: $endDate
    ) {
      invoiceNumber
      workOrderId
      franchiseName
      contactName
      contactEmail
      status
      billedAmount
      createdAt
      vendorName
      }
  }
`;

export const GET_SALES_COMMISSION_REPORT = gql`
  query getSalesCommissionReport(
    $userId: Int!,
    $franchiseIds: [Int!]!,
    $vendorIds: [Int!]!,
    $startDate: DateTime!,
    $endDate: DateTime!
  ) {
    getSalesCommissionReport(
      userId: $userId,
      franchiseIds: $franchiseIds,
      vendorIds: $vendorIds,
      startDate: $startDate,
      endDate: $endDate
    ) {
      invoiceNumber
      workOrderId
      franchiseName
      contactName
      contactEmail
      status
      billedAmount
      commissionBase
      commissionAmount
      createdAt
      vendorName
      }
  }
`;

export const GET_ALL_AR_REPORT = gql`
  query GetAllArReport(
    $startDate: DateTime!,
    $endDate: DateTime!,
    $franchiseIds: [Int!]!
  ) {
    getAllArReport(
      startDate: $startDate,
      endDate: $endDate,
      franchiseIds: $franchiseIds
    ) {
      invoiceNumber
      workOrderId
      franchiseName
      contactName
      contactEmail
      status
      billedAmount
      createdAt
      }
  }
`;

export const GET_PAYOUT_REPORTS = gql`
  query GetPayoutReports(
      $vanIds: [Int!]!
      $startDate: DateTime!,
      $endDate: DateTime!,
    ) {
      getPayoutReports(
        startDate: $startDate,
        endDate: $endDate,
        vanIds: $vanIds
      ){
        id,
        franchiseId,
        startDate,
        endDate,
        status,
        totalSales,
        totalRevenue,
        transactionFees,
        totalTax,
        marketingFee,
        royaltyFee,
        insurance,
        technologyFee,
        thirdPartyFleetFee,
        nationalFleetFee,
        creditGoOil,
        refunds,
        cashPayments,
        total,
        van {
          id,
          name,
        }
        franchise {
          id,
          name,
        }
      }
    }
`;

export const GET_FRANCHISE_VANS = gql`
  query GetFranchiseVans($franchiseId: Int!) {
    getFranchiseVans(franchiseId: $franchiseId) {
      id
      name
    }
  }
`;

export const RANK_AVAILABLE_VANS = gql`
  query RankAvailableVans($workOrderId: Int!, $gatherAllInfo: Boolean!) {
    rankAvailableVansForWorkOrder(
    workOrderId: $workOrderId, 
    gatherAllInfo: $gatherAllInfo
    ) {
      vanId
      vanName
      score
      appointmentCount
      drivingTimeFromPrevious
      drivingTimeToNext
      previousAppointmentSlot
      nextAppointmentSlot
      conflicts
    }
  }
`;

export const GET_USER_CREDIT = gql`
  query GetUserCredit($userId: Int!) {
    getUserCredit(userId: $userId) {
      discountCode {
        code
        discountType
      }
      discountCodeId
      credit
    }
  }
`;

export const GET_REFERRAL_CREDIT_VALUES = gql`
  query GetReferralCreditValues {
    getReferralCreditValues {
      name
      fee
    }
  }
`;

export const DISABLE_REFERRAL_POPUP = gql`
  mutation DisableReferralPopup {
    disableReferralPopup
  }
`;

export const IS_REFERRAL_POPUP_DISABLED = gql`
  query IsReferralPopupDisabled {
    isReferralPopupDisabled
  }
`;

export const PARSE_INVOICE_IMAGE = gql`
  mutation ParseInvoiceImage($imageUrl: String!) {
    parseInvoiceImage(imageUrl: $imageUrl) {
      ok
      error
      invoiceJson
    }
  }
`;

export const ADD_ITEMS_FROM_INVOICE = gql`
  mutation AddItemsFromInvoice(
    $franchiseId: Int!
    $vanId: Int
    $receiptImage: String!
    $partItems: [LoadPartItemInput!]!
    $oilItems: [LoadOilItemInput!]!
  ) {
    addItemsFromInvoice(
      franchiseId: $franchiseId
      vanId: $vanId
      receiptImage: $receiptImage
      partItems: $partItems
      oilItems: $oilItems
    ) {
      ok
      error
      inventoryUpdatesCount
    }
  }
`;

export const GET_FLEET_LOCATIONS = gql`
  query GetFleetLocations {
    getFleetLocations {
      id
      name
      street
      city
      province
      postalCode
      lat
      lng
    }
  }
`;

export const GET_FLEET_VEHICLES = gql`
  query GetFleetVehicles($locationId: Int, $withPhotos: Boolean) {
    getFleetVehicles(locationId: $locationId, withPhotos: $withPhotos) {
      id
      vin
      name
      fleetLocationId
      smartcarVehicleId
      frontLeftPhoto
      latestMileage
      hasPhoto
      vehicle {
        id
        year
        make
        model
        engine
      }
      subscriptions {
        id
        status
      }
    }
  }
`;

// Signed thumbnail URLs for just the vehicles currently on screen — the
// registry lazy-loads these per page instead of signing the whole fleet.
export const GET_FLEET_VEHICLE_PHOTOS = gql`
  query GetFleetVehiclePhotos($userVehicleIds: [Int!]!) {
    getFleetVehiclePhotos(userVehicleIds: $userVehicleIds) {
      userVehicleId
      url
    }
  }
`;

// Server-side paginated vehicle list: search/sort/filter/slice run in the DB
// and only one page ships to the browser — required for the whole-system
// master view, where the fleet is far too large to hold client-side.
export const GET_FLEET_VEHICLES_PAGE = gql`
  query GetFleetVehiclesPage(
    $offset: Int
    $limit: Int
    $sortKey: String
    $sortDir: String
    $filter: FleetVehicleFilterInput
  ) {
    getFleetVehiclesPage(
      offset: $offset
      limit: $limit
      sortKey: $sortKey
      sortDir: $sortDir
      filter: $filter
    ) {
      total
      rows {
        id
        vin
        name
        fleetLocationId
        smartcarVehicleId
        latestMileage
        hasPhoto
        vehicle {
          id
          year
          make
          model
          engine
        }
        subscriptions {
          id
          status
        }
      }
    }
  }
`;

// "Select all matching": the server applies the action to EVERY vehicle
// matching the filter — the client never holds tens of thousands of ids.
export const BULK_ASSIGN_FLEET_LOCATION_BY_FILTER = gql`
  mutation BulkAssignFleetLocationByFilter(
    $filter: FleetVehicleFilterInput
    $fleetLocationId: Int
  ) {
    bulkAssignFleetLocationByFilter(
      filter: $filter
      fleetLocationId: $fleetLocationId
    )
  }
`;

export const BULK_ACTIVATE_FLEET_SUBSCRIPTIONS_BY_FILTER = gql`
  mutation BulkActivateFleetSubscriptionsByFilter(
    $subscriptionTypeId: Int!
    $filter: FleetVehicleFilterInput
  ) {
    bulkActivateFleetSubscriptionsByFilter(
      subscriptionTypeId: $subscriptionTypeId
      filter: $filter
    ) {
      activated
      alreadyActive
      skipped
    }
  }
`;

// Whole-matching-set CSV, assembled server-side.
// Every rental booking for one vehicle (past, on-rent, upcoming) from the
// Departures & Returns uploads — the vehicle page's Rental history.
export const GET_FLEET_VEHICLE_RENTAL_HISTORY = gql`
  query GetFleetVehicleRentalHistory($userVehicleId: Int!) {
    getFleetVehicleRentalHistory(userVehicleId: $userVehicleId) {
      id
      departureDate
      returnDate
      departureCode
      returnCode
      returnName
    }
  }
`;

// Owner-only bulk exports (john+fraserway@gooil.ca): every completed invoice,
// and every inspection in full detail as a CSV.
export const GET_FLEET_INVOICES = gql`
  query GetFleetInvoices {
    getFleetInvoices {
      invoiceId
      tenantId
      invoiceNumber
      issuedAt
      total
      status
      vin
      unitNumber
      vehicleLabel
      franchise
      odometer
    }
  }
`;

export const EXPORT_FLEET_INSPECTIONS_CSV = gql`
  query ExportFleetInspectionsCsv {
    exportFleetInspectionsCsv
  }
`;

// Distinct year/make dropdown options for the server-paginated registry.
export const GET_FLEET_VEHICLE_FACETS = gql`
  query GetFleetVehicleFacets {
    getFleetVehicleFacets {
      years
      makes
    }
  }
`;

export const EXPORT_FLEET_VEHICLES_CSV = gql`
  query ExportFleetVehiclesCsv($filter: FleetVehicleFilterInput) {
    exportFleetVehiclesCsv(filter: $filter)
  }
`;

// Remote lock/unlock for a connected fleet vehicle (fleet-scope aware).
export const FLEET_LOCK_VEHICLE = gql`
  mutation FleetLockVehicle($userVehicleId: Int!) {
    fleetLockVehicle(userVehicleId: $userVehicleId)
  }
`;

export const FLEET_UNLOCK_VEHICLE = gql`
  mutation FleetUnlockVehicle($userVehicleId: Int!) {
    fleetUnlockVehicle(userVehicleId: $userVehicleId)
  }
`;

// Saved photo-orientation corrections, shared across all users/devices.
export const GET_FLEET_PHOTO_ROTATIONS = gql`
  query GetFleetPhotoRotations {
    getFleetPhotoRotations {
      objectKey
      rotation
    }
  }
`;

export const SET_FLEET_PHOTO_ROTATION = gql`
  mutation SetFleetPhotoRotation($objectKey: String!, $rotation: Int!) {
    setFleetPhotoRotation(objectKey: $objectKey, rotation: $rotation) {
      objectKey
      rotation
    }
  }
`;

export const GET_FLEET_STATS = gql`
  query GetFleetStats($locationId: Int) {
    getFleetStats(locationId: $locationId) {
      totalVehicles
      activeVehicles
      pendingVehicles
      attentionVehicles
      upcomingServices
      completedServices30d
      connectedVehicles
      systemView
      latestWorkOrderNumber
      services30d
      revenue30dCad
      revenue30dUsd
      lifetimeCad
      lifetimeUsd
      invoicedWorkOrdersCad
      invoicedWorkOrdersUsd
      arrCad
      arrUsd
      subscribedVehicles
      vehiclesToDate
      totalCustomers
      newCustomers30d
      salesByYear {
        year
        cad
        usd
      }
      fiscalSalesByYear {
        year
        cad
        usd
      }
      estGrossProfitCad
      estGrossProfitUsd
      grossMarginPct
      grossMarginSampleWos
      avgWorkOrderMinutes
      activeAlertsCount
      openReferralRepairs
      referralVehicles
      referralOpportunityCad
      repairsByCategory {
        category
        count
        vehicles
      }
    }
  }
`;

export const GET_FLEET_SERVICE_SCHEDULE = gql`
  query GetFleetServiceSchedule($locationId: Int, $status: String) {
    getFleetServiceSchedule(locationId: $locationId, status: $status) {
      id
      selfLogged
      fleetVisitNumber
      selfLoggedByName
      outsideShopName
      vonigoWorkOrderId
      status
      scheduledAt
      completedAt
      notes
      serviceLocationName
      serviceLocationSource
      unitLocationAtService
      accessOutcome
      accessOutcomeContact
      lineItems {
        id
        name
        service {
          id
          nameKey
        }
      }
      userVehicle {
        id
        vin
        name
        vehicle {
          make
          model
          year
        }
      }
      franchise {
        id
        name
      }
    }
  }
`;

export const UPDATE_VEHICLE_FLEET_LOCATION = gql`
  mutation UpdateVehicleFleetLocation($userVehicleId: Int!, $fleetLocationId: Int) {
    updateVehicleFleetLocation(
      userVehicleId: $userVehicleId
      fleetLocationId: $fleetLocationId
    ) {
      id
      fleetLocationId
    }
  }
`;

export const BULK_ASSIGN_FLEET_LOCATION = gql`
  mutation BulkAssignFleetLocation($userVehicleIds: [Int!]!, $fleetLocationId: Int) {
    bulkAssignFleetLocation(
      userVehicleIds: $userVehicleIds
      fleetLocationId: $fleetLocationId
    )
  }
`;

export const BULK_IMPORT_FLEET_VEHICLES = gql`
  mutation BulkImportFleetVehicles($vins: [String!]!, $locationId: Int) {
    bulkImportFleetVehicles(vins: $vins, locationId: $locationId) {
      vin
      status
      make
      model
      year
      error
    }
  }
`;

// Bulk "update from sheet": VIN-keyed CSV that updates existing vehicles'
// kilometers, location/depot, oil-life % and fuel, as of an optional date.
export const BULK_UPDATE_FLEET_VEHICLES_CSV = gql`
  mutation BulkUpdateFleetVehiclesCsv($csv: String!) {
    bulkUpdateFleetVehiclesCsv(csv: $csv) {
      rowsParsed
      vehiclesUpdated
      nameUpdated
      kmUpdated
      oilUpdated
      fuelUpdated
      locationUpdated
      vinsNotFound
      locationsNotMatched
      results {
        vin
        status
        updated
        locationNotMatched
        error
      }
    }
  }
`;

export const GET_FLEET_VEHICLE = gql`
  query GetFleetVehicle($id: Int!) {
    getFleetVehicle(id: $id) {
      id
      vin
      name
      fleetLocationId
      smartcarVehicleId
      vehicle {
        id
        year
        make
        model
        engine
      }
      mileage {
        id
        mileage
        createdAt
        eventType
        source
        workOrderId
      }
      subscriptions {
        id
        status
      }
      workOrders {
        id
        status
        scheduledAt
        completedAt
        notes
        selfLogged
        fleetVisitNumber
        selfLoggedByName
        outsideShopName
        franchise {
          id
          name
        }
        address {
          street
          city
          province
        }
        lineItems {
          id
          name
          quantity
          price
        }
        invoice {
          id
          invoiceNumber
          status
          billedAmount
          subTotal
          tax
          tenantId
          createdAt
        }
        inspections {
          id
          notes
          createdAt
          lat
          lng
          tireConditionFrontLeft
          tireConditionFrontRight
          tireConditionRearLeft
          tireConditionRearRight
          brakeConditionFrontLeft
          brakeConditionFrontRight
          brakeConditionRearLeft
          brakeConditionRearRight
          windshieldsCondition
          windshieldFluid
          brakeFluid
          leakInspection
          tireSize
          tireBrand {
            id
            name
          }
          tireBrandFrontLeft
          tireBrandFrontRight
          tireBrandRearLeft
          tireBrandRearRight
          tireBrandRearLeftInner
          tireBrandRearRightInner
          tireIdNumberFrontLeft
          tireIdNumberFrontRight
          tireIdNumberRearLeft
          tireIdNumberRearRight
          tireIdNumberRearLeftInner
          tireIdNumberRearRightInner
          engineHours
          isDually
          tireConditionRearLeftInner
          tireConditionRearRightInner
          preServiceOilLife
          oilLifeSource
          oilLifeReset
          stickerDueKm
          stickerDueDate
          rotationStatus
          rotationPattern
          rotationDeferReason
          rotationDeferNote
          rotationLockReason
          rotationVerified
          rotationVerifyNote
          measurementsJson
          partsUsedJson
          oilFilterId
          cabinFilterId
          engineFilterId
          awsLink {
            id
            vinLink
            vehicleLink
            mileageLink
            oilCapLink
            dipstickLocation
            vehicleLocationFrontLeft
            vehicleLocationFrontRight
            vehicleLocationRearLeft
            vehicleLocationRearRight
            tinLocationFrontLeft
            tinLocationFrontRight
            tinLocationRearLeft
            tinLocationRearRight
            oilFilterLocation
            oilLifeLocation
            oilLifeResetLocation
            windshieldLocation
          }
        }
        awsLinks {
          id
          vinLink
          vehicleLink
          mileageLink
          oilCapLink
          dipstickLocation
          vehicleLocationFrontLeft
          vehicleLocationFrontRight
          vehicleLocationRearLeft
          vehicleLocationRearRight
          tinLocationFrontLeft
          tinLocationFrontRight
          tinLocationRearLeft
          tinLocationRearRight
          oilFilterLocation
          oilLifeLocation
          oilLifeResetLocation
          windshieldLocation
        }
      }
    }
  }
`;

export const GET_FLEET_VEHICLE_LOCATION = gql`
  query GetFleetVehicleLocation($userVehicleId: Int!) {
    getFleetVehicleLocation(userVehicleId: $userVehicleId) {
      userVehicleId
      lat
      lng
      source
      updatedAt
    }
  }
`;

export const GET_FLEET_VEHICLE_LOCATION_HISTORY = gql`
  query GetFleetVehicleLocationHistory($userVehicleId: Int!) {
    getFleetVehicleLocationHistory(userVehicleId: $userVehicleId) {
      lat
      lng
      source
      label
      recordedAt
    }
  }
`;

export const DELETE_FLEET_LOCATION = gql`
  mutation DeleteFleetLocation($fleetLocationId: Int!) {
    deleteFleetLocation(fleetLocationId: $fleetLocationId)
  }
`;

export const GET_FLEET_VEHICLE_LOCATIONS = gql`
  query GetFleetVehicleLocations($locationId: Int) {
    getFleetVehicleLocations(locationId: $locationId) {
      userVehicleId
      lat
      lng
      source
      updatedAt
    }
  }
`;

// Every historical GPS ping across the fleet (telematics + service GPS) as
// anonymous points — feeds the heat map so it shows where the fleet actually
// operates over time, not just each vehicle's latest position.
export const GET_FLEET_HEAT_POINTS = gql`
  query GetFleetLocationHeatPoints($locationId: Int) {
    getFleetLocationHeatPoints(locationId: $locationId) {
      lat
      lng
    }
  }
`;

// Live health of the fleet's Smartcar connection (token usable? which
// permissions? how many vehicles have actually reported a position?) — shown
// under the map header so connection problems are visible in plain words.
export const GET_FLEET_TELEMATICS_STATUS = gql`
  query GetFleetTelematicsStatus {
    getFleetTelematicsStatus {
      connected
      located
      lastPingAt
      connectionOk
      permissions
      connectionError
      internal
    }
  }
`;

// ---------------------------------------------------------------------------
// Fleet — Repairs & Quotes
// Identified repair opportunities per vehicle (auto-derived from our inspection
// measurements + manual), each with the competing partner-shop quotes (PDFs).
// ---------------------------------------------------------------------------
const FLEET_SHOP_JOB_FIELDS = `
      id
      repairNumber
      category
      title
      detail
      priority
      status
      source
      estKmRemaining
      healthPercent
      approvedQuoteId
      quotesRequestedAt
      completedAt
      completedWhere
      completedCostCents
      completedOdometerKm
      completedNote
      externalServiceRecordId
      reinspectRequestedAt
      closedReason
      closedAt
      createdAt
      userVehicleId
      userVehicle {
        id
        vin
        name
        vehicle {
          year
          make
          model
        }
      }
      quotes {
        id
        shopName
        fileName
        pdfUrl
        totalBeforeTax
        currency
        marginPercent
        creditEarned
        status
        aiStatus
        createdAt
      }
`;

export const GET_FLEET_REPAIRS = gql`
  query GetFleetRepairs($locationId: Int, $includeClosed: Boolean) {
    getFleetRepairs(locationId: $locationId, includeClosed: $includeClosed) {
      ${FLEET_SHOP_JOB_FIELDS}
    }
  }
`;

// One shop job's page and its history (owner, 2026-10-29).
export const GET_FLEET_SHOP_JOB = gql`
  query FleetShopJob($itemId: Int!) {
    fleetShopJob(itemId: $itemId) {
      ${FLEET_SHOP_JOB_FIELDS}
    }
  }
`;

export const GET_FLEET_SHOP_JOB_HISTORY = gql`
  query FleetShopJobHistory($itemId: Int!) {
    fleetShopJobHistory(itemId: $itemId) {
      id
      kind
      label
      note
      byName
      createdAt
    }
  }
`;

export const REQUEST_FLEET_SHOP_QUOTES = gql`
  mutation RequestFleetShopQuotes($itemIds: [Int!]!) {
    requestFleetShopQuotes(itemIds: $itemIds) {
      jobs
      shopsEmailed
      shopsInPortal
    }
  }
`;

export const COMPLETE_FLEET_SHOP_JOBS = gql`
  mutation CompleteFleetShopJobs($input: CompleteShopJobsInput!) {
    completeFleetShopJobs(input: $input) {
      ${FLEET_SHOP_JOB_FIELDS}
    }
  }
`;

export const REINSPECT_FLEET_SHOP_JOB = gql`
  mutation ReinspectFleetShopJob($itemId: Int!, $note: String) {
    reinspectFleetShopJob(itemId: $itemId, note: $note) {
      ${FLEET_SHOP_JOB_FIELDS}
    }
  }
`;

export const SET_FLEET_SHOP_JOB_REINSPECTION = gql`
  mutation SetFleetShopJobReinspection($itemId: Int!, $passed: Boolean!, $note: String) {
    setFleetShopJobReinspection(itemId: $itemId, passed: $passed, note: $note) {
      ${FLEET_SHOP_JOB_FIELDS}
    }
  }
`;

export const CLOSE_FLEET_SHOP_JOBS = gql`
  mutation CloseFleetShopJobs($itemIds: [Int!]!, $reason: String!) {
    closeFleetShopJobs(itemIds: $itemIds, reason: $reason) {
      ${FLEET_SHOP_JOB_FIELDS}
    }
  }
`;

export const REOPEN_FLEET_SHOP_JOB = gql`
  mutation ReopenFleetShopJob($itemId: Int!, $reason: String!) {
    reopenFleetShopJob(itemId: $itemId, reason: $reason) {
      ${FLEET_SHOP_JOB_FIELDS}
    }
  }
`;

export const GET_FLEET_REPAIR_QUOTE_UPLOAD_URL = gql`
  mutation GetFleetRepairQuoteUploadUrl($fileName: String!) {
    getFleetRepairQuoteUploadUrl(fileName: $fileName) {
      presignedUrl
      key
    }
  }
`;

export const CREATE_FLEET_REPAIR_QUOTE = gql`
  mutation CreateFleetRepairQuote(
    $repairItemId: Int!
    $pdfKey: String!
    $fileName: String
    $shopName: String
    $totalBeforeTax: Float
    $marginPercent: Int
  ) {
    createFleetRepairQuote(
      repairItemId: $repairItemId
      pdfKey: $pdfKey
      fileName: $fileName
      shopName: $shopName
      totalBeforeTax: $totalBeforeTax
      marginPercent: $marginPercent
    ) {
      id
      shopName
      fileName
      pdfUrl
      totalBeforeTax
      currency
      marginPercent
      creditEarned
      status
      aiStatus
    }
  }
`;

export const APPROVE_FLEET_REPAIR_QUOTE = gql`
  mutation ApproveFleetRepairQuote($quoteId: Int!) {
    approveFleetRepairQuote(quoteId: $quoteId) {
      id
      status
      approvedQuoteId
    }
  }
`;

// A customer's own car: repairs our inspection flagged, partner shop quotes,
// choosing one (Aperio credit) and booking a recall (owner, 2026-10-30).
export const GET_MY_VEHICLE_REPAIRS = gql`
  query MyVehicleRepairs($userVehicleId: Int!) {
    myVehicleRepairs(userVehicleId: $userVehicleId) {
      ${FLEET_SHOP_JOB_FIELDS}
    }
  }
`;

export const REQUEST_MY_REPAIR_QUOTES = gql`
  mutation RequestMyRepairQuotes($itemId: Int!) {
    requestMyRepairQuotes(itemId: $itemId) {
      ${FLEET_SHOP_JOB_FIELDS}
    }
  }
`;

export const APPROVE_MY_REPAIR_QUOTE = gql`
  mutation ApproveMyRepairQuote($quoteId: Int!) {
    approveMyRepairQuote(quoteId: $quoteId) {
      ${FLEET_SHOP_JOB_FIELDS}
    }
  }
`;

export const BOOK_RECALL_FIX = gql`
  mutation BookRecallFix($userVehicleId: Int!, $source: String!, $campaignNumber: String!) {
    bookRecallFix(userVehicleId: $userVehicleId, source: $source, campaignNumber: $campaignNumber) {
      ${FLEET_SHOP_JOB_FIELDS}
    }
  }
`;

export const ADD_FLEET_REPAIR_ITEM = gql`
  mutation AddFleetRepairItem($input: AddRepairItemInput!) {
    addFleetRepairItem(input: $input) {
      id
      title
      category
      priority
      status
    }
  }
`;

export const DISMISS_FLEET_REPAIR_ITEM = gql`
  mutation DismissFleetRepairItem($itemId: Int!, $reason: String) {
    dismissFleetRepairItem(itemId: $itemId, reason: $reason) {
      id
      status
    }
  }
`;

// ---------------------------------------------------------------------------
// Partner Shop portal — the separate, stripped-down view shops log into.
// They see only vehicles within their service range, with VIN + condition
// photos + measurements, and upload a quote PDF.
// ---------------------------------------------------------------------------
export const GET_SHOP_REPAIR_JOBS = gql`
  query GetShopRepairJobs {
    getShopRepairJobs {
      shopName
      serviceRangeKm
      located
      jobs {
        repairItemId
        category
        title
        detail
        priority
        distanceKm
        vin
        vehicleLabel
        photoUrls
        quoteCount
        measurements {
          brakeMinMm
          tireMin32nds
          windshield
        }
        myQuote {
          id
          totalBeforeTax
          currency
          status
          aiStatus
          pdfUrl
        }
      }
    }
  }
`;

export const GET_SHOP_REPAIR_QUOTE_UPLOAD_URL = gql`
  mutation GetShopRepairQuoteUploadUrl($fileName: String!) {
    getShopRepairQuoteUploadUrl(fileName: $fileName) {
      presignedUrl
      key
    }
  }
`;

export const CREATE_SHOP_REPAIR_QUOTE = gql`
  mutation CreateShopRepairQuote(
    $repairItemId: Int!
    $pdfKey: String!
    $fileName: String
    $totalBeforeTax: Float
    $notes: String
  ) {
    createShopRepairQuote(
      repairItemId: $repairItemId
      pdfKey: $pdfKey
      fileName: $fileName
      totalBeforeTax: $totalBeforeTax
      notes: $notes
    ) {
      id
      totalBeforeTax
      currency
      status
      aiStatus
      pdfUrl
    }
  }
`;

// ---------------------------------------------------------------------------
// Fleet — Log service done elsewhere (outside work pooled into history)
// ---------------------------------------------------------------------------
// Everything read off a shop invoice; the fleet can type over any of it.
const FLEET_EXTERNAL_SERVICE_FIELDS = `
      id
      providerName
      providerAddress
      providerPhone
      invoiceNumber
      performedAt
      totalBeforeTax
      currency
      fileName
      pdfUrl
      aiStatus
      createdAt
      editedAt
      userVehicle {
        id
        vin
        name
        vehicle {
          year
          make
          model
        }
      }
      invoiceVin
      vehicleDescription
      vinMismatch
      vinConfirmed
      effectsAppliedAt
      suggestedVehicleId
      suggestedVehicleLabel
      odometerKm
      taxTotal
      totalWithTax
      techNotes
      notes
      recommendations
      lines {
        id
        name
        serviceType
        amount
        category
        partNumber
        quantity
        partsAmount
        labourAmount
      }
`;

export const GET_FLEET_EXTERNAL_SERVICES = gql`
  query GetFleetExternalServices($locationId: Int) {
    getFleetExternalServices(locationId: $locationId) {
      ${FLEET_EXTERNAL_SERVICE_FIELDS}
    }
  }
`;

export const UPDATE_FLEET_EXTERNAL_SERVICE = gql`
  mutation UpdateFleetExternalService(
    $id: Int!
    $fields: FleetExternalServiceFieldsInput
    $lines: [FleetExternalServiceLineInput!]
  ) {
    updateFleetExternalService(id: $id, fields: $fields, lines: $lines) {
      ${FLEET_EXTERNAL_SERVICE_FIELDS}
    }
  }
`;

export const MOVE_FLEET_EXTERNAL_SERVICE = gql`
  mutation MoveFleetExternalService($id: Int!, $userVehicleId: Int!) {
    moveFleetExternalService(id: $id, userVehicleId: $userVehicleId) {
      ${FLEET_EXTERNAL_SERVICE_FIELDS}
    }
  }
`;

export const CONFIRM_FLEET_EXTERNAL_SERVICE_VEHICLE = gql`
  mutation ConfirmFleetExternalServiceVehicle($id: Int!) {
    confirmFleetExternalServiceVehicle(id: $id) {
      ${FLEET_EXTERNAL_SERVICE_FIELDS}
    }
  }
`;

export const DELETE_FLEET_EXTERNAL_SERVICE = gql`
  mutation DeleteFleetExternalService($id: Int!) {
    deleteFleetExternalService(id: $id)
  }
`;

export const GET_FLEET_EXTERNAL_SERVICE_UPLOAD_URL = gql`
  mutation GetFleetExternalServiceUploadUrl($fileName: String!) {
    getFleetExternalServiceUploadUrl(fileName: $fileName) {
      presignedUrl
      key
    }
  }
`;

export const CREATE_FLEET_EXTERNAL_SERVICE = gql`
  mutation CreateFleetExternalService(
    $userVehicleId: Int!
    $pdfKey: String!
    $fileName: String
    $providerName: String
    $totalBeforeTax: Float
  ) {
    createFleetExternalService(
      userVehicleId: $userVehicleId
      pdfKey: $pdfKey
      fileName: $fileName
      providerName: $providerName
      totalBeforeTax: $totalBeforeTax
    ) {
      ${FLEET_EXTERNAL_SERVICE_FIELDS}
    }
  }
`;

// ---------------------------------------------------------------------------
// Fleet — canonical vehicle health/data store (source-agnostic) + CSV import
// Fleet staff sub-users (managed from the Staff tab).
export const GET_FLEET_STAFF = gql`
  query GetFleetStaff {
    getFleetStaff {
      id
      email
      name
      defaultFleetLocationId
      createdAt
      role
      addedByName
      isYou
      inHouseLast30
      inHouseTotal
      lastInHouseAt
      lastInHouseFv
      lastSignInAt
      lastSeenAt
    }
  }
`;

export const GET_MY_FLEET_DEFAULTS = gql`
  query GetMyFleetDefaults {
    getMyFleetDefaults {
      defaultFleetLocationId
      isStaff
      canManageStaff
      staffRole
    }
  }
`;

export const CREATE_FLEET_STAFF = gql`
  mutation CreateFleetStaff($email: String!, $password: String, $name: String, $defaultFleetLocationId: Int, $role: String) {
    createFleetStaff(email: $email, password: $password, name: $name, defaultFleetLocationId: $defaultFleetLocationId, role: $role) {
      id
      email
      name
      defaultFleetLocationId
      role
      inviteSent
    }
  }
`;

export const UPDATE_FLEET_STAFF = gql`
  mutation UpdateFleetStaff($id: Int!, $defaultFleetLocationId: Int, $password: String, $name: String, $role: String) {
    updateFleetStaff(id: $id, defaultFleetLocationId: $defaultFleetLocationId, password: $password, name: $name, role: $role) {
      id
      email
      name
      defaultFleetLocationId
      role
    }
  }
`;

export const DELETE_FLEET_STAFF = gql`
  mutation DeleteFleetStaff($id: Int!) {
    deleteFleetStaff(id: $id)
  }
`;

export const RESEND_FLEET_STAFF_INVITE = gql`
  mutation ResendFleetStaffInvite($id: Int!) {
    resendFleetStaffInvite(id: $id)
  }
`;

// Plans the fleet can put vehicles on + a bulk activate (no billing).
export const GET_FLEET_PLANS = gql`
  query GetFleetPlans {
    getFleetPlans {
      id
      name
      price
      frequency
    }
  }
`;

export const BULK_ACTIVATE_FLEET_SUBSCRIPTIONS = gql`
  mutation BulkActivateFleetSubscriptions($userVehicleIds: [Int!]!, $subscriptionTypeId: Int!) {
    bulkActivateFleetSubscriptions(userVehicleIds: $userVehicleIds, subscriptionTypeId: $subscriptionTypeId) {
      activated
      alreadyActive
      skipped
    }
  }
`;

// Add the standard fees (mobile service / supplies / environmental) the
// auto-bookings were missing to the fleet's existing work orders.
export const BACKFILL_FLEET_BOOKING_FEES = gql`
  mutation BackfillFleetBookingFees {
    backfillFleetBookingFees {
      workOrdersUpdated
      feesAdded
      oilRepriced
    }
  }
`;

// Link the fleet's VIN-imported vehicles (created bare, no recommended oil) to
// the matching catalog vehicle's oil + volume, so they price/inspect normally.
export const BACKFILL_FLEET_VEHICLE_OIL = gql`
  mutation BackfillFleetVehicleOil {
    backfillFleetVehicleOil {
      scanned
      matched
      unmatched
      mappings
    }
  }
`;

// One-click / bulk booking of an oil change + tire rotation to the nearest
// franchise/DSP. Only books vehicles within the service window (or with no
// odometer data); everything else is reported back in the result counts.
export const BOOK_FLEET_SERVICE = gql`
  mutation BookFleetService($userVehicleIds: [Int!]!, $date: DateTime) {
    bookFleetService(userVehicleIds: $userVehicleIds, date: $date) {
      requested
      booked
      inspections
      skippedNotDue
      skippedNoLocation
      skippedOutOfRange
      skippedExisting
      failed
      workOrderIds
      messages
    }
  }
`;

export const RESYNC_FLEET_PREDICTED_SCHEDULE = gql`
  mutation ResyncFleetPredictedSchedule {
    resyncFleetPredictedSchedule
  }
`;

export const BOOK_FLEET_PREDICTED_SERVICES = gql`
  mutation BookFleetPredictedServices($userVehicleIds: [Int!]!, $horizonDays: Int) {
    bookFleetPredictedServices(userVehicleIds: $userVehicleIds, horizonDays: $horizonDays) {
      requested
      booked
      inspections
      skippedNotDue
      skippedNoLocation
      skippedOutOfRange
      skippedExisting
      failed
      workOrderIds
      messages
    }
  }
`;

// Generic per-fleet custom fields (config + per-vehicle values) and the
// Departures & Returns importer (rental status + one-way location moves).
export const GET_FLEET_CUSTOM_FIELDS = gql`
  query GetFleetCustomFields {
    getFleetCustomFields {
      id
      fieldKey
      title
      enabled
      sortOrder
    }
  }
`;

export const GET_FLEET_VEHICLE_FIELDS = gql`
  query GetFleetVehicleFields {
    getFleetVehicleFields {
      userVehicleId
      fieldKey
      value
    }
  }
`;

export const SET_FLEET_CUSTOM_FIELD = gql`
  mutation SetFleetCustomField($fieldKey: String!, $title: String!, $enabled: Boolean!) {
    setFleetCustomField(fieldKey: $fieldKey, title: $title, enabled: $enabled) {
      id
      fieldKey
      title
      enabled
    }
  }
`;

// Owner-only odometer data-quality tooling: flagged readings with the dash
// photo beside each, OCR of that photo, and the one-click correction.
export const GET_FLEET_MILEAGE_AUDIT = gql`
  query GetFleetMileageAudit {
    getFleetMileageAudit {
      mileageId
      userVehicleId
      vin
      unitName
      eventType
      km
      at
      prevKm
      prevAt
      nextKm
      nextAt
      reason
      photoUrl
    }
  }
`;

export const SCAN_FLEET_ODOMETER_PHOTO = gql`
  mutation ScanFleetOdometerPhoto($mileageId: Int!, $photoUrl: String!) {
    scanFleetOdometerPhoto(mileageId: $mileageId, photoUrl: $photoUrl) {
      mileageId
      km
    }
  }
`;

export const FIX_FLEET_MILEAGE_ENTRY = gql`
  mutation FixFleetMileageEntry($mileageId: Int!, $correctedKm: Float, $remove: Boolean) {
    fixFleetMileageEntry(mileageId: $mileageId, correctedKm: $correctedKm, remove: $remove)
  }
`;

export const IMPORT_FLEET_DEPARTURES_RETURNS = gql`
  mutation ImportFleetDeparturesReturns($csv: String!) {
    importFleetDeparturesReturns(csv: $csv) {
      rowsParsed
      vehiclesUpdated
      onRent
      available
      locationsChanged
      kmReadingsLogged
      vehiclesCreated
      vinsNotFound
      locationsNotMatched
    }
  }
`;

// Per-vehicle "km until next service", keyed by userVehicleId on the client.
export const GET_FLEET_SERVICE_DUE = gql`
  query GetFleetServiceDue($locationId: Int, $userVehicleIds: [Int!]) {
    getFleetServiceDue(locationId: $locationId, userVehicleIds: $userVehicleIds) {
      userVehicleId
      intervalKm
      currentKm
      kmToService
      source
      kmPerDay
      kmPerDaySource
      predictedDueDate
      estKm
      estOilLife
      estimated
      dueBasis
      outsideServiceSuspected
      odometerConflict
      odometerConflictDetail
    }
  }
`;

// The account's make/model service-interval overrides + the mutations to manage
// them (used by the Service intervals settings screen).
export const GET_FLEET_SERVICE_INTERVALS = gql`
  query GetFleetServiceIntervals {
    getFleetServiceIntervals {
      id
      make
      model
      intervalKm
    }
  }
`;

export const SET_FLEET_SERVICE_INTERVAL = gql`
  mutation SetFleetServiceInterval($make: String!, $intervalKm: Int!, $model: String) {
    setFleetServiceInterval(make: $make, intervalKm: $intervalKm, model: $model) {
      id
      make
      model
      intervalKm
    }
  }
`;

export const DELETE_FLEET_SERVICE_INTERVAL = gql`
  mutation DeleteFleetServiceInterval($id: Int!) {
    deleteFleetServiceInterval(id: $id)
  }
`;

// ---------------------------------------------------------------------------
export const GET_FLEET_VEHICLES_HEALTH = gql`
  query GetFleetVehiclesHealth($locationId: Int) {
    getFleetVehiclesHealth(locationId: $locationId) {
      id
      userVehicleId
      oilLifeRemaining
      tireBrand
      tireSize
      odometer
      odometerUnit
      engineStatus
      healthStatus
      connectivityStatus
      source
      ingestedAt
    }
  }
`;

// Raw last-oil-change fact per vehicle (date @ odometer) shown on registry
// rows beside the derived oil-life % — a raw service record can't silently lie.
export const GET_FLEET_LAST_OIL_CHANGES = gql`
  query GetFleetLastOilChanges($locationId: Int) {
    getFleetLastOilChanges(locationId: $locationId) {
      userVehicleId
      km
      at
    }
  }
`;

export const GET_FLEET_VEHICLE_HEALTH = gql`
  query GetFleetVehicleHealth($userVehicleId: Int!) {
    getFleetVehicleHealth(userVehicleId: $userVehicleId) {
      tireBrand
      tireSize
      snapshot {
        id
        unitNumber
        oilLifeRemaining
        odometer
        odometerUnit
        engineHours
        engineStatus
        fuelBattery
        tirePressure
        healthStatus
        connectivityStatus
        source
        sourceLastUpdated
        ingestedAt
      }
      issues {
        id
        issue
        category
        code
        detail
        issueSource
        severity
        status
        lastSeenAt
      }
      profile {
        id
        licensePlate
        registrationProvince
        operatorName
        vehicleType
        bodyType
        trim
        color
        ownership
        groupName
        tags
        inServiceDate
        registrationExpiry
        insuranceExpiry
      }
    }
  }
`;

export const IMPORT_FLEET_VEHICLE_HEALTH_CSV = gql`
  mutation ImportFleetVehicleHealthCsv($csv: String!) {
    importFleetVehicleHealthCsv(csv: $csv) {
      vehiclesUpdated
      issuesUpserted
      rowsParsed
      vinsNotFound
    }
  }
`;

export const UPSERT_FLEET_VEHICLE_PROFILE = gql`
  mutation UpsertFleetVehicleProfile($userVehicleId: Int!, $input: VehicleProfileInput!) {
    upsertFleetVehicleProfile(userVehicleId: $userVehicleId, input: $input) {
      id
    }
  }
`;

// Active alerts — open vehicle-health issues across the fleet (recalls, DTCs,
// TPMS, DEF, service rules…), severity-ranked.
export const GET_FLEET_ACTIVE_ALERTS = gql`
  query GetFleetActiveAlerts($locationId: Int, $archived: Boolean) {
    getFleetActiveAlerts(locationId: $locationId, archived: $archived) {
      id
      userVehicleId
      vin
      vehicleLabel
      year
      make
      model
      unitNumber
      locationName
      issue
      detail
      category
      code
      issueSource
      severity
      status
      lastSeenAt
      tireBrand
      tireSize
    }
  }
`;

// Bring a dismissed alert back to the live feed (Archived view's Restore).
export const UNARCHIVE_FLEET_ALERT = gql`
  mutation UnarchiveFleetAlert($alertId: Int!) {
    unarchiveFleetAlert(alertId: $alertId)
  }
`;

// Dismiss/archive a telematics alert so it drops off the Active-alerts tab.
export const ARCHIVE_FLEET_ALERT = gql`
  mutation ArchiveFleetAlert($alertId: Int!) {
    archiveFleetAlert(alertId: $alertId)
  }
`;

// Per-vehicle data-feed log — every reading per data point over time, each
// coloured GREEN/YELLOW/RED by the backend measurement interpreter.
export const GET_FLEET_VEHICLE_DATA_LOG = gql`
  query GetFleetVehicleDataLog($userVehicleId: Int!) {
    getFleetVehicleDataLog(userVehicleId: $userVehicleId) {
      metric
      label
      value
      numericValue
      unit
      status
      source
      recordedAt
    }
  }
`;

// Owner-only: fold phantom duplicate-VIN rows (minted by staff logins) into
// the fleet's own vehicle. Real customers' rows are never touched.
export const MERGE_DUPLICATE_VIN_VEHICLES = gql`
  mutation MergeDuplicateVinVehicles($dryRun: Boolean) {
    mergeDuplicateVinVehicles(dryRun: $dryRun) {
      dryRun
      groups
      vehiclesRetired
      workOrdersMoved
      mileageMoved
      otherRowsMoved
      warnings
      details {
        vin
        keptOwnerEmail
        mergedOwnerEmails
      }
    }
  }
`;

/**
 * The signed-in customer's own referral card (Aperio preset): code, share
 * link, balance, the tenant's amounts, next expiry and credit history.
 */
export const GET_MY_REFERRAL_ACTIVITY = gql`
  query GetMyReferralActivity {
    getMyReferralActivity {
      code
      shareUrl
      balanceCents
      perPlanVehicleCents
      oneTimeOilChangeCents
      friendRewardCents
      expiryMonths
      nextExpiryAt
      nextExpiryCents
      brandName
      supportEmail
      entries {
        id
        kind
        amountCents
        createdAt
      }
    }
  }
`;

// ── Self-logged fleet services (the fleet's own techs; FV-#### numbers) ──
const SELF_SERVICE_ORDER_FIELDS = `
  id
  status
  notes
  scheduledAt
  completedAt
  selfLogged
  fleetVisitNumber
  address { street city countryCode postalCode province lat lng }
  vehicle { id year make model engine oilVolume }
  userVehicle { id userId vin name vehicleId }
  lineItems {
    id
    name
    price
    tax
    discountedPrice
    service { id name nameKey invoiceDescription serviceType { name } }
  }
  inspections {
    id
    inspectionType
    tireConditionFrontLeft
    tireConditionFrontRight
    tireConditionRearLeft
    tireConditionRearRight
    brakeConditionFrontLeft
    brakeConditionFrontRight
    brakeConditionRearLeft
    brakeConditionRearRight
    windshieldsCondition
    notes
    tireIdNumberFrontLeft
    tireIdNumberFrontRight
    tireIdNumberRearLeft
    tireIdNumberRearRight
    checkedFrontLeft
    checkedFrontRight
    checkedRearLeft
    checkedRearRight
    windshieldFluid
    brakeFluid
    leakInspection
    tireSize
    engineHours
    tireBrandId
    tireType
    isDually
    tireConditionRearLeftInner
    tireConditionRearRightInner
    tireIdNumberRearLeftInner
    tireIdNumberRearRightInner
    tireBrandFrontLeft
    tireBrandFrontRight
    tireBrandRearLeft
    tireBrandRearRight
    tireBrandRearLeftInner
    tireBrandRearRightInner
    measurementsJson
    partsUsedJson
    awsLink {
      id
      vinLink
      mileageLink
      oilCapLink
      dipstickLocation
      vehicleLocationFrontLeft
      vehicleLocationFrontRight
      vehicleLocationRearLeft
      vehicleLocationRearRight
      tinLocationFrontLeft
      tinLocationFrontRight
      tinLocationRearLeft
      tinLocationRearRight
      oilFilterLocation
      oilLifeLocation
      oilLifeResetLocation
      windshieldLocation
    }
  }
`;

export const START_FLEET_SELF_SERVICE = gql`
  mutation StartFleetSelfService($userVehicleId: Int!, $main: String!, $addOns: [String!]) {
    startFleetSelfService(userVehicleId: $userVehicleId, main: $main, addOns: $addOns) {
      ${SELF_SERVICE_ORDER_FIELDS}
    }
  }
`;

export const GET_FLEET_SELF_SERVICE = gql`
  query GetFleetSelfService($id: Int!) {
    getFleetSelfService(id: $id) {
      ${SELF_SERVICE_ORDER_FIELDS}
    }
  }
`;

export const GET_OPEN_FLEET_SELF_SERVICES = gql`
  query GetOpenFleetSelfServices($userVehicleId: Int) {
    getOpenFleetSelfServices(userVehicleId: $userVehicleId) {
      id
      fleetVisitNumber
      scheduledAt
      lineItems { id name }
      userVehicle { id name vin }
    }
  }
`;

export const COMPLETE_FLEET_SELF_SERVICE = gql`
  mutation CompleteFleetSelfService($id: Int!, $partsJson: String) {
    completeFleetSelfService(id: $id, partsJson: $partsJson) {
      id
      status
      fleetVisitNumber
    }
  }
`;

export const DISCARD_FLEET_SELF_SERVICE = gql`
  mutation DiscardFleetSelfService($id: Int!) {
    discardFleetSelfService(id: $id)
  }
`;

export const GET_FLEET_PART_SUGGESTIONS = gql`
  query GetFleetPartSuggestions($userVehicleId: Int!) {
    getFleetPartSuggestions(userVehicleId: $userVehicleId) {
      oil
      oilLitres
      oilFilter
      engineAirFilter
      cabinAirFilter
      lastUsed { oil oilLitres oilFilter engineAirFilter cabinAirFilter }
      spec { oil oilLitres oilFilter engineAirFilter cabinAirFilter }
    }
  }
`;

export const SEARCH_FLEET_SERVICE_VEHICLES = gql`
  query SearchFleetServiceVehicles($q: String) {
    searchFleetServiceVehicles(q: $q) {
      id
      name
      vin
      year
      make
      model
      location
    }
  }
`;

/**
 * What a consumer-skin booking stored on its work order (unit / buzzer,
 * extras to quote, an unverified car or an engine "to be confirmed"),
 * asked separately from GET_WORK_ORDER so the work-order page keeps
 * working against a server without the field (docs/consumer-skin-api.md).
 */
export const GET_WORK_ORDER_CONSUMER_BOOKING = gql`
  query GetWorkOrderConsumerBooking($id: Int!) {
    getWorkOrderById(id: $id) {
      id
      consumerBooking {
        skin
        engineStatus
        vehicleUnverifiedText
        vehicleUnverifiedKind
        engineAckHigherPrice
        engineHigherPriceCents
        engineHigherLabel
        engineConfirmFlag
        unit
        extrasRequested
        vehicleMatchedBy
        carSuggestion
        carSuggestionSource
      }
    }
  }
`;

/** The technician confirms the car's engine at the visit (staff only). */
export const CONFIRM_WORK_ORDER_ENGINE = gql`
  mutation ConfirmWorkOrderEngine($workOrderId: Int!, $vehicleId: Int!) {
    confirmWorkOrderEngine(workOrderId: $workOrderId, vehicleId: $vehicleId) {
      workOrderId
      vehicleId
      engineStatus
      flagged
      flag
    }
  }
`;

/** Sam's chat (components/SamChat): the signed-in customer's first name, status and car. */
export const GET_SAM_CHAT_VARIABLES = gql`
  query SamChatVariables {
    samChatVariables {
      firstName
      customerStatus
      vehicle
    }
  }
`;

export const GET_ON_THE_WAY_TEXT = gql`
  query OnTheWayText($workOrderId: Int!) {
    onTheWayText(workOrderId: $workOrderId) {
      available
      sentAt
    }
  }
`;

export const SEND_ON_THE_WAY_TEXT = gql`
  mutation SendOnTheWayText($workOrderId: Int!) {
    sendOnTheWayText(workOrderId: $workOrderId) {
      sent
      reason
    }
  }
`;

// The shared mandatory inspection: a no-login link an outside shop fills in
// for one vehicle (server: fleet-shop-inspection.resolver.ts). The checklist,
// results, what is missing and the signed photo urls are JSON strings.
const FLEET_SHOP_INSPECTION_FIELDS = `
  id
  userVehicleId
  vehicleLabel
  shopName
  shopEmail
  emailedAt
  repairItemId
  status
  link
  createdAt
  startedAt
  completedAt
  odometerKm
  techName
  externalServiceRecordId
  workOrderId
  checklistJson
  resultsJson
  missingJson
  photoUrlsJson
`;

export const GET_FLEET_SHOP_INSPECTIONS = gql`
  query FleetShopInspections($userVehicleId: Int) {
    fleetShopInspections(userVehicleId: $userVehicleId) {
      ${FLEET_SHOP_INSPECTION_FIELDS}
    }
  }
`;

export const CREATE_FLEET_SHOP_INSPECTION = gql`
  mutation CreateFleetShopInspection($userVehicleId: Int!, $shopName: String, $shopEmail: String, $repairItemId: Int) {
    createFleetShopInspection(userVehicleId: $userVehicleId, shopName: $shopName, shopEmail: $shopEmail, repairItemId: $repairItemId) {
      ${FLEET_SHOP_INSPECTION_FIELDS}
    }
  }
`;

export const EMAIL_FLEET_SHOP_INSPECTION = gql`
  mutation EmailFleetShopInspection($id: Int!, $shopEmail: String!) {
    emailFleetShopInspection(id: $id, shopEmail: $shopEmail) {
      ${FLEET_SHOP_INSPECTION_FIELDS}
    }
  }
`;

export const GET_FLEET_INSPECTION_REQUIREMENT = gql`
  query FleetInspectionRequirement {
    fleetInspectionRequirement { everyMonths options canManage }
  }
`;

export const SET_FLEET_INSPECTION_REQUIREMENT = gql`
  mutation SetFleetInspectionRequirement($everyMonths: Int) {
    setFleetInspectionRequirement(everyMonths: $everyMonths) { everyMonths options canManage }
  }
`;

export const GET_FLEET_INSPECTION_STATUS = gql`
  query FleetInspectionStatus {
    fleetInspectionStatus { userVehicleId lastAt dueOn due months }
  }
`;

export const CANCEL_FLEET_SHOP_INSPECTION = gql`
  mutation CancelFleetShopInspection($id: Int!) {
    cancelFleetShopInspection(id: $id)
  }
`;

export const GET_FLEET_SHOP_INSPECTION_CHECKLIST = gql`
  query FleetShopInspectionChecklist {
    fleetShopInspectionChecklist {
      standardItems
      customItems
    }
  }
`;

export const SET_FLEET_SHOP_INSPECTION_ITEMS = gql`
  mutation SetFleetShopInspectionItems($items: [String!]!) {
    setFleetShopInspectionItems(items: $items)
  }
`;

export const GET_WORK_ORDER_VEHICLE_CLASS = gql`
  query WorkOrderVehicleClass($workOrderId: Int!) {
    workOrderVehicleClass(workOrderId: $workOrderId) {
      dutyClass
      source
      gvwrLb
      override
    }
  }
`;

export const SET_WORK_ORDER_VEHICLE_CLASS = gql`
  mutation SetWorkOrderVehicleClass($workOrderId: Int!, $dutyClass: String) {
    setWorkOrderVehicleClass(workOrderId: $workOrderId, dutyClass: $dutyClass) {
      dutyClass
      source
      gvwrLb
      override
    }
  }
`;

// The work order's "Message customer" panel (server:
// customer-messages.resolver.ts): the kinds of text staff can send, and the
// customer's texts, calls and emails, newest first.
export const GET_CUSTOMER_MESSAGES = gql`
  query CustomerMessages($workOrderId: Int!) {
    customerMessages(workOrderId: $workOrderId) {
      phone
      email
      canText
      reason
      options {
        type
        label
        preview
        problem
      }
      timeline {
        id
        at
        channel
        direction
        title
        body
        summary
        transcript
        status
        durationSeconds
        sentBy
        recordingId
      }
    }
  }
`;

export const SEND_CUSTOMER_TEXT = gql`
  mutation SendCustomerText(
    $workOrderId: Int!
    $type: String!
    $minutes: Int
    $message: String
  ) {
    sendCustomerText(
      workOrderId: $workOrderId
      type: $type
      minutes: $minutes
      message: $message
    ) {
      sent
      reason
      body
    }
  }
`;

// ─── Vehicle record: customer's own cars + the tech's car on a work order ───
// Server: VehicleRecordResolver. The customer's screens never ask for the oil
// grade or filter part numbers (our product spec, the fleet rule) nor for who
// did the work; the tech's screen does ask for the filters.
const VEHICLE_RECORD_INSPECTION_FIELDS = `
  id
  notes
  createdAt
  tireConditionFrontLeft
  tireConditionFrontRight
  tireConditionRearLeft
  tireConditionRearRight
  brakeConditionFrontLeft
  brakeConditionFrontRight
  brakeConditionRearLeft
  brakeConditionRearRight
  windshieldsCondition
  windshieldFluid
  brakeFluid
  leakInspection
  tireSize
  tireBrand {
    id
    name
  }
  tireBrandFrontLeft
  tireBrandFrontRight
  tireBrandRearLeft
  tireBrandRearRight
  tireBrandRearLeftInner
  tireBrandRearRightInner
  tireIdNumberFrontLeft
  tireIdNumberFrontRight
  tireIdNumberRearLeft
  tireIdNumberRearRight
  tireIdNumberRearLeftInner
  tireIdNumberRearRightInner
  engineHours
  isDually
  tireConditionRearLeftInner
  tireConditionRearRightInner
  preServiceOilLife
  oilLifeSource
  oilLifeReset
  stickerDueKm
  stickerDueDate
  rotationStatus
  rotationPattern
  rotationDeferReason
  rotationDeferNote
  rotationLockReason
  rotationVerified
  rotationVerifyNote
`;

const VEHICLE_RECORD_PHOTO_FIELDS = `
  id
  vinLink
  vehicleLink
  mileageLink
  oilCapLink
  dipstickLocation
  vehicleLocationFrontLeft
  vehicleLocationFrontRight
  vehicleLocationRearLeft
  vehicleLocationRearRight
  tinLocationFrontLeft
  tinLocationFrontRight
  tinLocationRearLeft
  tinLocationRearRight
  oilFilterLocation
  oilLifeLocation
  oilLifeResetLocation
  windshieldLocation
`;

export const GET_MY_VEHICLES = gql`
  query MyVehicles {
    myVehicles {
      userVehicleId
      photoUrl
      photoRotation
      odometerKm
      odometerAt
      lastServiceAt
      lastServiceWorkOrderId
      nextVisitAt
      nextVisitWorkOrderId
      visitCount
      photoCount
      latestInspectionWorkOrderId
      lastServiceKm
      unit
      recentServiceDates
      userVehicle {
        id
        vin
        name
        vehicle {
          id
          year
          make
          model
          engine
        }
        subscriptions {
          id
          status
        }
      }
      latestInspection {
        ${VEHICLE_RECORD_INSPECTION_FIELDS}
      }
    }
  }
`;

const vehicleRecordQuery = (withPartSpecs: boolean) => `
  photoUrl
  photoRotation
  licensePlate
  plateRegion
  visitServices {
    workOrderId
    services
  }
  photoRotations {
    objectKey
    rotation
  }
  userVehicle {
    id
    vin
    name
    latestMileage
    vehicle {
      id
      year
      make
      model
      engine
    }
    workOrders {
      id
      status
      scheduledAt
      completedAt
      mileage {
        id
        mileage
      }
      lineItems {
        id
        name
        quantity
        price
      }
      invoice {
        id
        invoiceNumber
        status
        billedAmount
      }
      inspections {
        ${VEHICLE_RECORD_INSPECTION_FIELDS}
        ${withPartSpecs ? 'oilFilterId\n        cabinFilterId\n        engineFilterId' : ''}
        awsLink {
          ${VEHICLE_RECORD_PHOTO_FIELDS}
        }
      }
      awsLinks {
        ${VEHICLE_RECORD_PHOTO_FIELDS}
      }
    }
  }
`;

export const GET_MY_VEHICLE_RECORD = gql`
  query MyVehicleRecord($userVehicleId: Int!) {
    myVehicleRecord(userVehicleId: $userVehicleId) {
      ${vehicleRecordQuery(false)}
    }
  }
`;

// The customer's own car's visits as reports (same shape as
// GET_FLEET_VISIT_REPORTS): reportJson = { report, fixes, tireForecasts }.
export const GET_MY_VISIT_REPORTS = gql`
  query MyVehicleVisitReports($userVehicleId: Int!) {
    myVehicleVisitReports(userVehicleId: $userVehicleId) {
      workOrderId
      inspectionId
      date
      odometer
      unit
      kind
      score
      colour
      videoUrl
      invoiceId
      invoiceTenantId
      invoiceNumber
      reportJson
    }
  }
`;

export const GET_WORK_ORDER_VEHICLE_RECORD = gql`
  query WorkOrderVehicleRecord($workOrderId: Int!) {
    workOrderVehicleRecord(workOrderId: $workOrderId) {
      ${vehicleRecordQuery(true)}
    }
  }
`;

export const GET_WORK_ORDER_VEHICLE_PHOTOS = gql`
  query WorkOrderVehiclePhotos($workOrderIds: [Int!]!) {
    workOrderVehiclePhotos(workOrderIds: $workOrderIds) {
      workOrderId
      url
      rotation
    }
  }
`;

// Per-fleet modules (feature switches): what this fleet's dashboard shows.
// A module that is off hides its buttons, columns and menu items.
export const GET_MY_FLEET_MODULES = gql`
  query GetMyFleetModules {
    getMyFleetModules {
      customerType
      modules {
        key
        label
        whoFor
        description
        enabled
        paid
        available
        requested
        parent
      }
    }
  }
`;

export const REQUEST_FLEET_MODULE = gql`
  mutation RequestFleetModule($key: String!) {
    requestFleetModule(key: $key)
  }
`;

// The fleet's own logo (owner, 2026-10-30): header + Settings -> Your logo.
export const GET_FLEET_LOGO_URL = gql`
  query FleetLogoUrl {
    fleetLogoUrl
  }
`;

export const FLEET_LOGO_UPLOAD_URL = gql`
  mutation FleetLogoUploadUrl($contentType: String!) {
    fleetLogoUploadUrl(contentType: $contentType) {
      presignedUrl
      key
    }
  }
`;

export const SET_FLEET_LOGO = gql`
  mutation SetFleetLogo($key: String) {
    setFleetLogo(key: $key)
  }
`;

// Insurance and registration on file (vehicle-document). Photos are only
// ever shown through 10-minute signed links, to the owner and fleet admins.
const DOCUMENT_UPLOAD_TARGET = `
  presignedUrl
  key
  contentType
  headers {
    name
    value
  }
`;

const DOCUMENT_STATES = `
  states {
    kind
    colour
    reason
    expiresOn
    daysLeft
  }
`;

export const GET_WORK_ORDER_DOCUMENT_STEP = gql`
  query WorkOrderDocumentStep($workOrderId: Int!) {
    workOrderDocumentStep(workOrderId: $workOrderId) {
      required
      reason
      done
      ${DOCUMENT_STATES}
    }
  }
`;

export const WORK_ORDER_DOCUMENT_UPLOAD_URL = gql`
  mutation WorkOrderDocumentUploadUrl($workOrderId: Int!) {
    workOrderDocumentUploadUrl(workOrderId: $workOrderId) {
      ${DOCUMENT_UPLOAD_TARGET}
    }
  }
`;

export const SAVE_WORK_ORDER_DOCUMENTS = gql`
  mutation SaveWorkOrderDocuments($workOrderId: Int!, $input: WorkOrderDocumentsInput!) {
    saveWorkOrderDocuments(workOrderId: $workOrderId, input: $input)
  }
`;

const VEHICLE_DOCUMENTS_VIEW = `
  userVehicleId
  vehicleLabel
  consent
  colour
  ${DOCUMENT_STATES}
  documents {
    id
    kind
    source
    readStatus
    insurer
    policyNumber
    expiryDate
    registrationExpiry
    vinMismatch
    createdAt
    url
    contentType
  }
`;

export const GET_VEHICLE_DOCUMENTS = gql`
  query VehicleDocuments($userVehicleId: Int!) {
    vehicleDocuments(userVehicleId: $userVehicleId) {
      ${VEHICLE_DOCUMENTS_VIEW}
    }
  }
`;

export const VEHICLE_DOCUMENT_UPLOAD_URL = gql`
  mutation VehicleDocumentUploadUrl($userVehicleId: Int!, $pdf: Boolean) {
    vehicleDocumentUploadUrl(userVehicleId: $userVehicleId, pdf: $pdf) {
      ${DOCUMENT_UPLOAD_TARGET}
    }
  }
`;

export const ADD_VEHICLE_DOCUMENT = gql`
  mutation AddVehicleDocument(
    $userVehicleId: Int!
    $kind: String!
    $key: String!
    $consent: Boolean
  ) {
    addVehicleDocument(
      userVehicleId: $userVehicleId
      kind: $kind
      key: $key
      consent: $consent
    ) {
      ${VEHICLE_DOCUMENTS_VIEW}
    }
  }
`;

export const GET_MY_DOCUMENT_CONSENT = gql`
  query MyDocumentConsent {
    myDocumentConsent
  }
`;

export const SET_DOCUMENT_CONSENT = gql`
  mutation SetDocumentConsent($consent: Boolean!, $source: String) {
    setDocumentConsent(consent: $consent, source: $source)
  }
`;

export const GET_VEHICLE_DOCUMENT_STATUSES = gql`
  query VehicleDocumentStatuses($userVehicleIds: [Int!]!) {
    vehicleDocumentStatuses(userVehicleIds: $userVehicleIds) {
      userVehicleId
      colour
      ${DOCUMENT_STATES}
    }
  }
`;

export const FLEET_DOCUMENT_BULK_UPLOAD_URL = gql`
  mutation FleetDocumentBulkUploadUrl($pdf: Boolean) {
    fleetDocumentBulkUploadUrl(pdf: $pdf) {
      ${DOCUMENT_UPLOAD_TARGET}
    }
  }
`;

export const ADD_FLEET_BULK_DOCUMENT = gql`
  mutation AddFleetBulkDocument($key: String!, $consent: Boolean) {
    addFleetBulkDocument(key: $key, consent: $consent) {
      documentId
      userVehicleId
    }
  }
`;

export const GET_DOCUMENT_LINK = gql`
  query DocumentLink($code: String!) {
    documentLink(code: $code) {
      vehicleLabel
      consent
      ${DOCUMENT_STATES}
    }
  }
`;

export const DOCUMENT_LINK_UPLOAD_URL = gql`
  mutation DocumentLinkUploadUrl($code: String!, $pdf: Boolean) {
    documentLinkUploadUrl(code: $code, pdf: $pdf) {
      ${DOCUMENT_UPLOAD_TARGET}
    }
  }
`;

export const SUBMIT_DOCUMENT_LINK = gql`
  mutation SubmitDocumentLink(
    $code: String!
    $kind: String!
    $key: String!
    $consent: Boolean
  ) {
    submitDocumentLink(code: $code, kind: $kind, key: $key, consent: $consent)
  }
`;

// ---- Safety recalls (NHTSA + Transport Canada, owner 2026-10-29) ----
const RECALL_FIELDS = `
  userVehicleId
  checked
  matchedBy
  matchedModel
  open
  recalls {
    source
    campaignNumber
    component
    summary
    consequence
    remedy
    reportDate
    parkIt
    parkOutside
    status
    statusNote
    statusAt
    url
  }
`;

export const GET_VEHICLE_RECALLS = gql`
  query VehicleRecalls($userVehicleIds: [Int!]!) {
    vehicleRecalls(userVehicleIds: $userVehicleIds) { ${RECALL_FIELDS} }
  }
`;

export const GET_VEHICLE_RECALL_COUNTS = gql`
  query VehicleRecallCounts($userVehicleIds: [Int!]!) {
    vehicleRecallCounts(userVehicleIds: $userVehicleIds) { userVehicleId open parkIt }
  }
`;

export const GET_WORK_ORDER_RECALLS = gql`
  query WorkOrderRecalls($workOrderId: Int!) {
    workOrderRecalls(workOrderId: $workOrderId) { ${RECALL_FIELDS} }
  }
`;

export const SET_VEHICLE_RECALL_STATUS = gql`
  mutation SetVehicleRecallStatus(
    $userVehicleId: Int!
    $source: String!
    $campaignNumber: String!
    $status: String!
    $note: String
  ) {
    setVehicleRecallStatus(
      userVehicleId: $userVehicleId
      source: $source
      campaignNumber: $campaignNumber
      status: $status
      note: $note
    ) { ${RECALL_FIELDS} }
  }
`;

// One login's sign-ins (owner, 2026-10-29), for the fleet team list.
export const GET_FLEET_SIGN_IN_HISTORY = gql`
  query FleetSignInHistory($userId: Int!) {
    fleetSignInHistory(userId: $userId) { at method device }
  }
`;

// Fleet Settings: morning health email and automatic booking (owner, 2026-10-29).
const FLEET_HEALTH_EMAIL_FIELDS = `
  sendHour timeZone lastSentDay canManage hours
  zones { zone label }
  team { userId name email on self sendHour timeZone }
`;
export const GET_FLEET_HEALTH_EMAIL = gql`
  query FleetHealthEmail {
    fleetHealthEmail { ${FLEET_HEALTH_EMAIL_FIELDS} }
  }
`;
export const SET_FLEET_HEALTH_EMAIL = gql`
  mutation SetFleetHealthEmail($userId: Int, $on: Boolean, $sendHour: Int, $timeZone: String) {
    setFleetHealthEmail(userId: $userId, on: $on, sendHour: $sendHour, timeZone: $timeZone) { ${FLEET_HEALTH_EMAIL_FIELDS} }
  }
`;
export const SEND_FLEET_HEALTH_EMAIL_NOW = gql`
  mutation SendFleetHealthEmailNow {
    sendFleetHealthEmailNow
  }
`;
const FLEET_AUTO_BOOK_FIELDS = `
  enabled daysAhead moduleOn blockedReason canManage lastRunAt lastBooked lastMessage dayOptions
`;
export const GET_FLEET_AUTO_BOOK = gql`
  query FleetAutoBook {
    fleetAutoBook { ${FLEET_AUTO_BOOK_FIELDS} }
  }
`;
export const SET_FLEET_AUTO_BOOK = gql`
  mutation SetFleetAutoBook($enabled: Boolean!, $daysAhead: Int) {
    setFleetAutoBook(enabled: $enabled, daysAhead: $daysAhead) { ${FLEET_AUTO_BOOK_FIELDS} }
  }
`;

// ---- Drivers sign fleet vehicles out and in (owner, 2026-10-29) ----------

export const GET_DRIVER_HOME = gql`
  query DriverHome {
    driverHome {
      name
      fleetName
      enabled
      locationConsent
      isManager
      itemsJson
      photoSlotsJson
      vehicles {
        id
        label
        unitName
        vin
        plate
        outWithMe
        outAt
        checkoutId
        outKm
        lastCheckAt
        lastResult
        dailyDue
      }
    }
  }
`;

export const DRIVER_FIND_VEHICLES = gql`
  query DriverFindVehicles($q: String!) {
    driverFindVehicles(q: $q) {
      id
      label
      unitName
      vin
      plate
      outWithMe
      outWithName
      outAt
    }
  }
`;

export const SET_DRIVER_LOCATION_CONSENT = gql`
  mutation SetDriverLocationConsent($allowed: Boolean!) {
    setDriverLocationConsent(allowed: $allowed)
  }
`;

export const START_DRIVER_CHECK = gql`
  mutation StartDriverCheck($userVehicleId: Int!, $kind: String!) {
    startDriverCheck(userVehicleId: $userVehicleId, kind: $kind) {
      id
      kind
      answersJson
      missingJson
      outKm
      vehicle {
        id
        label
        unitName
        vin
        plate
        outWithMe
        outWithName
        outAt
      }
    }
  }
`;

export const DRIVER_CHECK_PHOTO_UPLOAD = gql`
  mutation DriverCheckPhotoUpload($checkId: Int!, $contentType: String!) {
    driverCheckPhotoUpload(checkId: $checkId, contentType: $contentType) {
      presignedUrl
      key
      at
    }
  }
`;

export const SAVE_DRIVER_CHECK = gql`
  mutation SaveDriverCheck($checkId: Int!, $answersJson: String!) {
    saveDriverCheck(checkId: $checkId, answersJson: $answersJson)
  }
`;

export const SUBMIT_DRIVER_CHECK = gql`
  mutation SubmitDriverCheck(
    $checkId: Int!
    $answersJson: String!
    $lat: Float
    $lng: Float
    $accuracyM: Float
    $takeOver: Boolean
  ) {
    submitDriverCheck(
      checkId: $checkId
      answersJson: $answersJson
      lat: $lat
      lng: $lng
      accuracyM: $accuracyM
      takeOver: $takeOver
    ) {
      complete
      missingJson
      outWithName
      result
      problems
    }
  }
`;

export const GET_FLEET_DRIVER_BOARD = gql`
  query FleetDriverBoard {
    fleetDriverBoard {
      out {
        checkoutId
        userVehicleId
        vehicleLabel
        plate
        driverName
        outAt
        outKm
        located
        lastCheckAt
        lastResult
        checkCurrent
        checkNote
      }
      recent {
        id
        userVehicleId
        vehicleLabel
        driverName
        kind
        result
        problems
        odometerKm
        windshield
        located
        completedAt
      }
    }
  }
`;

export const GET_FLEET_VEHICLE_DRIVER_CHECKS = gql`
  query FleetVehicleDriverChecks($userVehicleId: Int!) {
    fleetVehicleDriverChecks(userVehicleId: $userVehicleId) {
      id
      userVehicleId
      vehicleLabel
      driverName
      kind
      result
      problems
      odometerKm
      windshield
      located
      completedAt
    }
  }
`;

export const GET_FLEET_DRIVER_CHECK = gql`
  query FleetDriverCheck($id: Int!) {
    fleetDriverCheck(id: $id) {
      id
      userVehicleId
      vehicleLabel
      driverName
      kind
      result
      odometerKm
      completedAt
      lat
      lng
      accuracyM
      answersJson
      defectsJson
      photoUrlsJson
      itemsJson
    }
  }
`;

export const GET_FLEET_VEHICLE_PLATES = gql`
  query FleetVehiclePlates {
    fleetVehiclePlates {
      userVehicleId
      licensePlate
      registrationProvince
    }
  }
`;

export const SET_FLEET_VEHICLE_PLATE = gql`
  mutation SetFleetVehiclePlate($userVehicleId: Int!, $licensePlate: String, $registrationProvince: String) {
    setFleetVehiclePlate(userVehicleId: $userVehicleId, licensePlate: $licensePlate, registrationProvince: $registrationProvince) {
      userVehicleId
      licensePlate
      registrationProvince
    }
  }
`;

// Tire and brake forecast on the fleet vehicle page.
// Every finished visit's inspection as the vehicle page draws it, latest
// first (utils/visit-report.ts): reportJson = { report, fixes, tireForecasts }.
export const GET_FLEET_VISIT_REPORTS = gql`
  query FleetVehicleVisitReports($userVehicleId: Int!) {
    fleetVehicleVisitReports(userVehicleId: $userVehicleId) {
      workOrderId
      inspectionId
      date
      odometer
      unit
      kind
      score
      colour
      videoUrl
      invoiceId
      invoiceTenantId
      invoiceNumber
      reportJson
    }
  }
`;

export const GET_FLEET_WEAR_FORECAST = gql`
  query FleetWearForecast($id: Int!) {
    fleetWearForecast(id: $id) {
      part
      current
      currentAt
      replaceAt
      kmLeft
      dueDate
      estimate
      kmPerUnit
      kmPerYear
      basis
    }
  }
`;

export const GET_FLEET_VEHICLE_TRACKER = gql`
  query FleetVehicleTracker($id: Int!) {
    fleetVehicleTracker(id: $id) {
      imei
      model
      lastSeenAt
      lastRecordAt
      ignition
      voltageMv
      odometerKm
      online
    }
  }
`;

// The customer's own car as Smartcar last reported it (Account -> vehicle page).
export const GET_MY_CONNECTED_CAR = gql`
  query MyConnectedCar($userVehicleId: Int!) {
    myConnectedCar(userVehicleId: $userVehicleId) {
      connected
      smartcarVehicleId
      odometerKm
      oilLifeRemaining
      fuel
      tirePressure
      readAt
      lat
      lng
      locatedAt
    }
  }
`;
